// Pre-defining the componentHandler interface, for documentation and
// static verification.
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _type_of(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
}
var komodoVersion = "1.0.0-beta";
export var componentHandler = {
    // Window Information
    windowInfo: {},
    // Upgrade DOM
    // Searches existing DOM for elements of our component type and upgrades
    // them if they have not already been ugraded.
    //
    // @param {string=} optJsClass the programatic name of the element class
    // we need to create a new instance of.
    // @param {string=} optCssClass the name of the CSS Class elements of this
    // type will have.
    upgradeDom: function upgradeDom(optJsClass, optCssClass) {},
    // Upgrade Element
    // Upgrades a specific element rather than all in the DOM.
    //
    // @param {!Element} element The element we wish to upgrade
    // @param {string=} optJsClass Optional name of the class we want to upgrade
    // the element to.
    upgradeElement: function upgradeElement(element, optCssClass) {},
    // UpgradeElements
    // Upgrades a specific list of elements rather than all in the DOM.
    //
    // @param {!Element|!Array<!Element>|!NodeList|!HTMLCollection} element.
    // The elements we wish to upgrade.
    upgradeElements: function upgradeElements(elements) {},
    // Uphrade All Registered
    // Upgrades all registered components found in the Current DOM. This is
    // automatically called on window load.
    upgradeAllRegistered: function upgradeAllRegistered() {},
    // Register Upgraded CallBack
    // Allows user to be alerted to any ypgrades that are performed for a given
    // component type.
    //
    // @param {string} jsClass The class name of the Komodo component we wish to
    // hook into for any upgrades performed.
    // @param {function(!HTMLElement)} callback The function to call upon an upgrade.
    // This funciton should expect 1 parameter - the HTMLElement which got upgraded.
    registerUpgradedCallback: function registerUpgradedCallback(jsClass, callback) {},
    // Register
    // Registers a class for future use and attempts to upgrade existing DOM.
    //
    // @param {componentHandler.ComponentConfigPublic} config the registration configuration
    register: function register(config) {},
    // Downgrade Elements
    // Downgrade either a given node, an array of nodes, or a NodeList.
    //
    // @param {!Node|!Array<!Node>|!NodeList} nodes
    downgradeElements: function downgradeElements(nodes) {},
    createEvent: function createEvent(e, t, o, s) {
        return new CustomEvent(e, {
            detail: t,
            bubbles: o,
            cancelable: s
        });
    }
};
componentHandler = function() {
    'use strict';
    // Window Information
    function windowInfoInternal() {
        return {
            screen_width: window.innerWidth,
            screen_height: window.innerHeight
        };
    }
    // Breakpoints Information
    // Return breakpoint keys and their max-width. SCSS breakpoints keys are mapped to the min-width for optimized CSS.
    // So make the necessary adjustments when breakpoints are updated.
    function breakpoints() {
        return {
            xs: 391,
            sm: 577,
            md: 769,
            lg: 993,
            xl: 1201
        };
    }
    // Keyboard Key Codes
    // These keyboard key codes will be used to dictate keyboard behavior for the components.
    function keyCodes() {
        return {
            ENTER: 13,
            ESCAPE: 27,
            SPACE: 32,
            END: 35,
            HOME: 36,
            ARROW_LEFT: 37,
            ARROW_UP: 38,
            ARROW_RIGHT: 39,
            ARROW_DOWN: 40
        };
    }
    // Dispatch an event when the breakpoints change on resize.
    function breakpointMonitor() {
        window.componentHandler.currentBreakpoint = window.componentHandler.getBreakpoint();
        window.addEventListener('resize', function(e) {
            if (window.componentHandler.currentBreakpoint !== window.componentHandler.getBreakpoint()) {
                window.componentHandler.currentBreakpoint = window.componentHandler.getBreakpoint();
                document.dispatchEvent(componentHandler.createEvent('komodoResized', {
                    currentBreakpoint: window.componentHandler.currentBreakpoint
                }, false, false));
                window.componentHandler.upgradeDom();
            }
        });
    }
    function getBreakpoint() {
        var _$breakpoints = window.componentHandler.breakpoints;
        var keys = Object.keys(_$breakpoints);
        for(var i = 0; i < keys.length; i++){
            var key = keys[i];
            var size = _$breakpoints[key];
            if (window.innerWidth < size) {
                return key;
            }
        }
        return "hd";
    }
    // @type {!Array<componentHandler.ComponentConfig}
    var registeredComponents_ = [];
    // @type {!Array<componentHandler.Component>}
    var createdComponents_ = [];
    // All created instances of Komodo Components
    var componentInstances = {};
    var componentConfigProperty_ = 'komodoComponentConfigInternal_';
    // Searches registered components for a class we are interested in using.
    // Optionally replaces a match with passed object if specificed.
    //
    // @param {string} name the name of a class we want to use.
    // @param {componentHandler.ComponentConfig=} optReplace Optional object to replace match with.
    // @return {!Object|boolean}
    // @private
    function findRegisteredClass_(name, optReplace) {
        for(var i = 0; i < registeredComponents_.length; i++){
            if (registeredComponents_[i].className === name) {
                if (typeof optReplace !== 'undefined') {
                    registeredComponents_[i] = optReplace;
                }
                return registeredComponents_[i];
            }
        }
        return false;
    }
    // Returns an array of the classnames of the upgraded class on the element.
    //
    // @param {!Element} element The element to fetch data from.
    // @return {!Array<string>}
    // @private
    function getUpgradedListOfElement_(element) {
        var dataUpgraded = element.getAttribute('data-upgraded');
        // Use `['']` as default value to conform the `,name,name...` style.
        return dataUpgraded === null ? [
            ''
        ] : dataUpgraded.split(',');
    }
    // Returns true if the given element has already been upgraded for the given
    // class.
    //
    // @param {!Element} element The element we want to check.
    // @param {string} jsClass The class to check for.
    // @returns {boolean}
    // @private
    function isElementUpgraded_(element, jsClass) {
        var upgradedList = getUpgradedListOfElement_(element);
        return upgradedList.indexOf(jsClass) !== -1;
    }
    // Create an event object.
    //
    // @param {string} eventType The type name of the event.
    // @param {boolean} bubbles Whether the event should bubble up the DOM.
    // @param {boolean} cancelable Whether the event can be canceled.
    // @returns {!Event}
    function createEvent_(eventType, detail, bubbles, cancelable) {
        var event;
        if (typeof CustomEvent === "function") {
            event = new CustomEvent(eventType, {
                detail: detail,
                bubbles: bubbles,
                cancelable: cancelable
            });
        } else {
            event = document.createEvent('CustomEvent');
            event.initCustomEvent(eventType, bubbles, cancelable, detail);
        }
        return event;
    }
    // Searches existing DOM for elements of our component type and upgrades them
    // if they have not already been upgraded.
    //
    // @param {string=} optJsClass the programatic name of the element class we
    // need to create a new instance of.
    // @param {string=} optCssClass the name of the CSS class elements of this
    // type will have.
    function upgradeDomInternal(optJsClass, optCssClass) {
        if (typeof optJsClass === 'undefined' && typeof optCssClass === 'undefined') {
            for(var i = 0; i < registeredComponents_.length; i++){
                upgradeDomInternal(registeredComponents_[i].className, registeredComponents_[i].cssClass);
            }
        } else {
            var jsClass = /** @type {string} */ optJsClass;
            if (typeof optCssClass === 'undefined') {
                var registeredClass = findRegisteredClass_(jsClass);
                if (registeredClass) {
                    optCssClass = registeredClass.cssClass;
                }
            }
            var elements = document.querySelectorAll('.' + optCssClass);
            for(var i = 0; i < elements.length; i++){
                var element = elements[i];
                if (window.componentHandler.validateForUpgrade(element)) {
                    upgradeElementInternal(element, jsClass);
                } else {
                    window.componentHandler.downgradeElements(element);
                }
            }
        }
    }
    // Validate if upgrade ready. If not, downgrade the element.
    // 
    // @param {!Element} element
    // The element we wish to validate for upgrade readiness.
    // Checks against breakpoints and associated configuration.
    function validateForUpgradeInternal(element) {
        var _$breakpoints = Object.keys(window.componentHandler.breakpoints);
        var currentBreakpoint = _$breakpoints.indexOf(window.componentHandler.currentBreakpoint);
        var hasStartBreakpoint = element.dataset.hasOwnProperty('komodoBreakpointStart');
        var hasEndBreakpoint = element.dataset.hasOwnProperty('komodoBreakpointEnd');
        var startBreakpoint = _$breakpoints.indexOf(element.dataset.komodoBreakpointStart);
        var endBreakpoint = _$breakpoints.indexOf(element.dataset.komodoBreakpointEnd);
        if (hasStartBreakpoint && hasEndBreakpoint && currentBreakpoint >= startBreakpoint && currentBreakpoint < endBreakpoint) {
            return true;
        } else if ((hasStartBreakpoint || hasEndBreakpoint) && (hasStartBreakpoint && !hasEndBreakpoint && currentBreakpoint >= startBreakpoint || hasEndBreakpoint && !hasStartBreakpoint && currentBreakpoint < endBreakpoint)) {
            return true;
        } else if (!hasStartBreakpoint && !hasEndBreakpoint) {
            return true;
        } else {
            return false;
        }
    }
    // Upgrades a specific element rather than all in the DOM.
    //
    // @param {!Element} element The element we wish to upgrade.
    // @param {string=} optJsClass Optional name of the class we want to upgrade
    // the element to.
    function upgradeElementInternal(element, optJsClass) {
        // Verify argument type.
        if (!((typeof element === "undefined" ? "undefined" : _type_of(element)) === 'object' && _instanceof(element, Element))) {
            throw new Error('Invalid argument provided to upgrade Komodo element.');
        }
        // Allow upgrade to be canceled by canceling emitted event.
        var upgradingEv = createEvent_('komodo-componentupgrading', null, true, true);
        element.dispatchEvent(upgradingEv);
        if (upgradingEv.defaultPrevented) {
            return;
        }
        var upgradedList = getUpgradedListOfElement_(element);
        var classesToUpgrade = [];
        // If jsClass is not provided scan the registered components to find the
        // ones matching the element's CSS classList.
        if (!optJsClass) {
            var classList = element.classList;
            registeredComponents_.forEach(function(component) {
                // Match CSS & Not to be upgraded & Not upgraded.
                if (classList.contains(component.cssClass) && classesToUpgrade.indexOf(component) === -1 && !isElementUpgraded_(element, component.className)) {
                    classesToUpgrade.push(component);
                }
            });
        } else if (!isElementUpgraded_(element, optJsClass)) {
            classesToUpgrade.push(findRegisteredClass_(optJsClass));
        }
        // Upgrade the element for each classes.
        for(var i = 0, n = classesToUpgrade.length, registeredClass; i < n; i++){
            registeredClass = classesToUpgrade[i];
            if (registeredClass) {
                // Mark element as upgraded.
                var componentId = "data-" + registeredClass.className.toLowerCase().replace('komodo', 'komodo-') + "-component-id";
                upgradedList.push(registeredClass.className);
                element.setAttribute('data-upgraded', upgradedList.join(','));
                element.setAttribute(componentId, Math.random().toString(36).slice(2));
                var instance = new registeredClass.classConstructor(element);
                componentInstances[registeredClass.className] = componentInstances[registeredClass.className] || {};
                componentInstances[registeredClass.className][element.getAttribute(componentId)] = instance;
                instance[componentConfigProperty_] = registeredClass;
                createdComponents_.push(instance);
                // Call any callbacks the user has registered with this component type.
                for(var j = 0, m = registeredClass.callbacks.length; j < m; j++){
                    registeredClass.callbacks[j](element);
                }
                if (registeredClass.widget) {
                    // Assign per element instance for control over API
                    element[registeredClass.className] = instance;
                }
            } else {
                throw new Error('Unable to find a registered component for the given class.');
            }
            var upgradedEv = createEvent_('komodo-componentupgraded', true, false);
            element.dispatchEvent(upgradedEv);
        }
    }
    //
    // Upgrades a specific list of elements rather than all in the DOM.
    //
    // @param {!Element|!Array<!Element>|!NodeList|!HTMLCollection} elements
    // The elements we wish to upgrade.
    //
    function upgradeElementsInternal(elements) {
        if (!Array.isArray(elements)) {
            if (_instanceof(elements, Element)) {
                elements = [
                    elements
                ];
            } else {
                elements = Array.prototype.slice.call(elements);
            }
        }
        for(var i = 0, n = elements.length, element; i < n; i++){
            element = elements[i];
            if (_instanceof(element, HTMLElement)) {
                upgradeElementInternal(element);
                if (element.children.length > 0) {
                    upgradeElementsInternal(element.children);
                }
            }
        }
    }
    //
    // Registers a class for future use and attempts to upgrade existing DOM.
    //
    // @param {componentHandler.ComponentConfigPublic} config
    //
    function registerInternal(config) {
        // In order to support both Closure-compiled and uncompiled code accessing
        // this method, we need to allow for both the dot and array syntax for
        // property access. You'll therefore see the `foo.bar || foo['bar']`
        // pattern repeated across this method.
        var widgetMissing = typeof config.widget === 'undefined' && typeof config['widget'] === 'undefined';
        var widget = true;
        if (!widgetMissing) {
            widget = config.widget || config['widget'];
        }
        var newConfig = /** @type {componentHandler.ComponentConfig} */ {
            classConstructor: config.constructor || config['constructor'],
            className: config.classAsString || config['classAsString'],
            cssClass: config.cssClass || config['cssClass'],
            widget: widget,
            callbacks: []
        };
        registeredComponents_.forEach(function(item) {
            if (item.cssClass === newConfig.cssClass) {
                throw new Error('The provided cssClass has already been registered: ' + item.cssClass);
            }
            if (item.className === newConfig.className) {
                throw new Error('The provided className has already been registered');
            }
        });
        if (config.constructor.prototype.hasOwnProperty(componentConfigProperty_)) {
            throw new Error('Komodo component classes must not have ' + componentConfigProperty_ + ' defined as a property.');
        }
        var found = findRegisteredClass_(config.classAsString, newConfig);
        if (!found) {
            registeredComponents_.push(newConfig);
        }
    }
    // Allows user to be alerted to any upgrades that are performed for a given
    // component type
    //
    // @param {string} jsClass The class name of the MDL component we wish
    // to hook into for any upgrades performed.
    // @param {function(!HTMLElement)} callback The function to call upon an
    // upgrade. This function should expect 1 parameter - the HTMLElement which
    // got upgraded.
    function registerUpgradedCallbackInternal(jsClass, callback) {
        var regClass = findRegisteredClass_(jsClass);
        if (regClass) {
            regClass.callbacks.push(callback);
        }
    }
    // Upgrades all registered components found in the current DOM. This is
    // automatically called on window load.
    function upgradeAllRegisteredInternal() {
        for(var n = 0; n < registeredComponents_.length; n++){
            upgradeDomInternal(registeredComponents_[n].className);
        }
    }
    // Check the component for the downgrade method.
    // Execute if found.
    // Remove component from createdComponents list.
    //
    // @param {?componentHandler.Component} component
    function deconstructComponentInternal(component) {
        if (component) {
            var componentIndex = createdComponents_.indexOf(component);
            createdComponents_.splice(componentIndex, 1);
            var upgrades = component.element_.getAttribute('data-upgraded').split(',');
            var componentPlace = upgrades.indexOf(component[componentConfigProperty_].classAsString);
            upgrades.splice(componentPlace, 1);
            component.element_.setAttribute('data-upgraded', upgrades.join(','));
            component.element_.removeAttribute('data-' + component.komodoComponentConfigInternal_.className.toLowerCase() + '-component-id');
            component.destroy();
            var ev = createEvent_('komodo-componentdowngraded', true, false);
            component.element_.dispatchEvent(ev);
        }
    }
    // Downgrade either a given node, an array of nodes, or a NodeList.
    //
    // @param {!Node|!Array<!Node>|!NodeList} nodes
    function downgradeNodesInternal(nodes) {
        //
        // Auxiliary function to downgrade a single node.
        // @param  {!Node} node the node to be downgraded
        //
        var downgradeNode = function downgradeNode(node) {
            createdComponents_.filter(function(item) {
                return item.element_ === node;
            }).forEach(deconstructComponentInternal);
        };
        if (_instanceof(nodes, Array) || _instanceof(nodes, NodeList)) {
            for(var n = 0; n < nodes.length; n++){
                downgradeNode(nodes[n]);
            }
        } else if (_instanceof(nodes, Node)) {
            downgradeNode(nodes);
        } else {
            throw new Error('Invalid argument provided to downgrade Komodo nodes.');
        }
    }
    // Now return the functions that should be made public with their publicly
    // facing names...
    return {
        windowInfo: windowInfoInternal,
        breakpoints: breakpoints,
        keyCodes: keyCodes,
        breakpointMonitor: breakpointMonitor,
        getBreakpoint: getBreakpoint,
        createEvent: createEvent_,
        validateForUpgrade: validateForUpgradeInternal,
        upgradeDom: upgradeDomInternal,
        upgradeElement: upgradeElementInternal,
        upgradeElements: upgradeElementsInternal,
        upgradeAllRegistered: upgradeAllRegisteredInternal,
        registerUpgradedCallback: registerUpgradedCallbackInternal,
        register: registerInternal,
        downgradeElements: downgradeNodesInternal,
        registeredInstances: componentInstances
    };
}();
//
// Describes the type of a registered component type managed by
// componentHandler. Provided for benefit of the Closure compiler.
//
// @typedef {{
//  constructor: Function,
//  classAsString: string,
//  cssClass: string,
//  widget: (string|boolean|undefined)
// }}
//
componentHandler.ComponentConfigPublic; // jshint ignore:line
//
// Describes the type of a registered component type managed by
// componentHandler. Provided for benefit of the Closure compiler.
//
// @typedef {{
//  constructor: !Function,
//  className: string,
//  cssClass: string,
//  widget: (string|boolean),
//  callbacks: !Array<function(!HTMLElement)>
// }}
//
componentHandler.ComponentConfig; // jshint ignore:line
//
//
// Created component (i.e., upgraded element) type as managed by
// componentHandler. Provided for benefit of the Closure compiler.
//
// @typedef {{
//  element_: !HTMLElement,
//  className: string,
//  classAsString: string,
//  cssClass: string,
//  widget: string
// }}
componentHandler.Component; // jshint ignore:line
// Export all symbols, for the benefit of Closure compiler.
// No effect on uncompiled code.
componentHandler['windowInfo'] = componentHandler.windowInfo;
componentHandler['breakpoints'] = componentHandler.breakpoints();
componentHandler['keyCodes'] = componentHandler.keyCodes();
componentHandler['currentBreakpoint'] = '';
componentHandler['breakpointMonitor'] = componentHandler.breakpointMonitor;
componentHandler['getBreakpoint'] = componentHandler.getBreakpoint;
componentHandler['createEvent'] = componentHandler.createEvent;
componentHandler['validateForUpgrade'] = componentHandler.validateForUpgrade;
componentHandler['upgradeDom'] = componentHandler.upgradeDom;
componentHandler['upgradeElement'] = componentHandler.upgradeElement;
componentHandler['upgradeElements'] = componentHandler.upgradeElements;
componentHandler['upgradeAllRegistered'] = componentHandler.upgradeAllRegistered;
componentHandler['registerUpgradedCallback'] = componentHandler.registerUpgradedCallback;
componentHandler['register'] = componentHandler.register;
componentHandler['downgradeElements'] = componentHandler.downgradeElements;
window.componentHandler = componentHandler;
window['componentHandler'] = componentHandler;
componentHandler.breakpointMonitor();
// Class constructor for Komodo Accordion Item component.
// Implements Komodo component design pattern
// 
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Accordion Item.
var KomodoAccordionItem = function KomodoAccordionItem(element) {
    this.element_ = element;
    this.element_.setAttribute('tabindex', 0);
    this.header = this.element_.querySelector("." + this.cssClasses_.HEADER);
    this.body = this.element_.querySelector("." + this.cssClasses_.BODY);
    this.body.setAttribute('tabindex', 0);
    this.toggle = this.toggle.bind(this);
    this.header.addEventListener('click', this.toggle);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.element_.addEventListener('keydown', this.onKeyDown);
};
window['KomodoAccordionItem'] = KomodoAccordionItem;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoAccordionItem.prototype.cssClasses_ = {
    OPEN: 'open',
    HEADER: 'kmd-accordion__item-header',
    BODY: 'kmd-accordion__item-body'
};
// Toggle the accordion.
// 
// @public
KomodoAccordionItem.prototype.toggle = function(event) {
    if (event) {
        event.preventDefault();
    }
    if (this.element_.classList.contains(this.cssClasses_.OPEN)) {
        this.close();
    } else {
        this.open();
    }
};
KomodoAccordionItem.prototype['toggle'] = KomodoAccordionItem.prototype.toggle;
// Open the accordion.
// 
// @public
KomodoAccordionItem.prototype.open = function() {
    this.element_.dispatchEvent(componentHandler.createEvent('opening.komodo.accordion', {
        target: this.element_
    }, false, true));
    this.element_.classList.add(this.cssClasses_.OPEN);
    this.element_.dispatchEvent(componentHandler.createEvent('opened.komodo.accordion', {
        target: this.element_
    }, false, true));
};
KomodoAccordionItem.prototype['open'] = KomodoAccordionItem.prototype.open;
// Close the accordion.
// 
// @public
KomodoAccordionItem.prototype.close = function() {
    this.element_.dispatchEvent(componentHandler.createEvent('closing.komodo.accordion', {}, false, true));
    this.element_.classList.remove(this.cssClasses_.OPEN);
    this.element_.dispatchEvent(componentHandler.createEvent('closed.komodo.accordion', {}, false, true));
};
KomodoAccordionItem.prototype['close'] = KomodoAccordionItem.prototype.close;
// Bind the keyboard events with the accordion.
// 
// @private
KomodoAccordionItem.prototype.onKeyDown = function(event) {
    switch(event.keyCode){
        case window.componentHandler.keyCodes.SPACE:
            this.toggle(event);
            break;
        case window.componentHandler.keyCodes.ARROW_UP:
            if (event.target.previousElementSibling) {
                event.target.previousElementSibling.focus();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_DOWN:
            if (event.target.nextElementSibling) {
                event.target.nextElementSibling.focus();
            }
            break;
    }
};
// Deconstruct the accordion item component.
// 
// @public
KomodoAccordionItem.prototype.destroy = function() {
    this.header.removeEventListener('click', this.toggle);
};
KomodoAccordionItem.prototype['destroy'] = KomodoAccordionItem.prototype.destroy;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoAccordionItem,
    classAsString: 'KomodoAccordionItem',
    cssClass: 'kmd-accordion__item',
    widget: true
});
// Class constructor for Komodo Accordion component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Accordion.
var KomodoAccordion = function KomodoAccordion(element) {
    this.element_ = element;
    this.accordions = [];
    if (this.element_.dataset.komodoOpenOne) {
        this.openAccordion = null;
    }
    this.element_.setAttribute('tabindex', 0);
    // Open Accordions
    var accordions = this.element_.getElementsByClassName(this.cssClasses_.ACCORDION);
    for(var i = 0; i < accordions.length; i++){
        var accordion = accordions[i];
        var accordionItem = componentHandler.registeredInstances.KomodoAccordionItem[accordion.dataset.komodoAccordionitemComponentId];
        this.accordions.push(accordionItem);
        if (this.element_.dataset.komodoOpenOne) {
            accordion.addEventListener("opened.komodo.accordion", (function(event) {
                if (this.openAccordion) {
                    if (this.openAccordion.element_ !== event.target) {
                        this.openAccordion.close();
                    }
                }
                this.openAccordion = componentHandler.registeredInstances.KomodoAccordionItem[event.target.dataset.komodoAccordionitemComponentId];
            }).bind(this));
        }
    }
    this.onKeyDown = this.onKeyDown.bind(this);
    this.element_.addEventListener('keydown', this.onKeyDown);
};
window['KomodoAccordion'] = KomodoAccordion;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoAccordion.prototype.cssClasses_ = {
    OPEN: 'open',
    ACCORDION: 'kmd-accordion__item',
    HEADER: 'kmd-accordion__item-header',
    BODY: 'kmd-accordion__item-body'
};
// Bind the keyboard events with the accordion.
// 
// @private
KomodoAccordion.prototype.onKeyDown = function(event) {
    switch(event.keyCode){
        case window.componentHandler.keyCodes.ENTER:
            this.accordions[0].element_.focus();
            break;
        case window.componentHandler.keyCodes.ARROW_UP:
            if (event.target.previousElementSibling) {
                event.target.previousElementSibling.focus();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_DOWN:
            if (event.target.nextElementSibling) {
                event.target.nextElementSibling.focus();
            }
            break;
    }
};
// Deconstruct the accordion component.
// 
// @public
KomodoAccordion.prototype.destroy = function() {
    for(var i = 0; i < this.accordions.length; i++){
        this.accordions[i].destroy();
    }
};
KomodoAccordion.prototype['destroy'] = KomodoAccordion.prototype.destroy;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoAccordion,
    classAsString: 'KomodoAccordion',
    cssClass: 'kmd-accordion',
    widget: true
});
var globalInteractiveElements = [];
var documentElement = document.documentElement;
var iniY = null;
var swipeScroll = 0;
var offsetScreenWidth;
function handleTouchStart1(e) {
    iniY = e.touches[0].clientY;
}
function handleTouchMove1(e) {
    if (iniY == null) {
        return;
    }
    var currentTouch = e.touches[0];
    var currentY = currentTouch.clientY - iniY;
    if (currentY > 0) {
        swipeScroll = 1;
    } else if (currentY < 0) {
        swipeScroll = 1;
    }
    iniY = null;
}
documentElement.addEventListener('touchstart', handleTouchStart1);
documentElement.addEventListener('touchmove', handleTouchMove1);
// end
document.addEventListener('DOMContentLoaded', function() {
// console.log('Carousel V7.13.24-6')
// var startTime = performance.now();
// var carousels = validateCarousels();
// if (!carousels.length) {
// console.error("No carousel elements found.");
// console.log("Waiting for event or timeout...");
// var eventReceived = false;
// var firstTimeoutId, secondTimeoutId;
// firstTimeoutId = setTimeout(function() {
//   if (!eventReceived) {
//     console.log("First Timeout reached. Executing after waiting.");
//     var carouselsRetry = validateCarousels();
//     if (!carouselsRetry.length) {
//       console.error("No carousel elements found after first try.");
//       secondTimeoutId = setTimeout(function() {
//         if (!eventReceived) {
//           console.log("Additional 3.5 seconds reached. Executing final check.");
//           var carouselsFinalRetry = validateCarousels();
//           if (!carouselsFinalRetry.length) {
//             console.error("No carousel elements found after final retry.");
//           } else {
//             console.log("Carousel elements found after final retry. Executing initCarouselIE2.");
//             initCarouselIE2();
//           }
//         }
//       }, 3500);
//     } else {
//       console.log("Carousel elements found after retrying. Executing initCarouselIE2.");
//       initCarouselIE2();
//     }
//   }
// }, 3500);
// document.addEventListener('at-content-rendering-succeeded', function() {
// var endTime = performance.now();
// var loadTime = endTime - startTime;
// console.log("Event 'at-content-rendering-succeeded' received. Load time: " + loadTime + "ms. Executing now.");
//
// if (firstTimeoutId) {
//   clearTimeout(firstTimeoutId);
//   console.log("First timeout cleared.");
// }
// if (secondTimeoutId) {
//   clearTimeout(secondTimeoutId);
//   console.log("Second timeout cleared.");
// }
//
// var carouselsRetry = validateCarousels();
// if (!carouselsRetry.length) {
//   console.error("No carousel elements found after receiving event.");
// } else {
//   console.log("Carousel elements found after receiving event. Executing initCarouselIE2.");
//   initCarouselIE2();
// }
// });
// } else {
// console.log("Carousels found on initial validation. Executing directly.");
// initCarouselIE2();
// }
});
// function handleContentRenderingSucceeded(event) {
//   eventReceived = true;
//   console.log("Event 'contentRenderingSucceeded' received. Executing now.");
//   var carouselsRetry = validateCarousels();
//   if (!carouselsRetry.length) {
//     console.error("No carousel elements found after receiving event.");
//   } else {
//     console.log("Carousel elements found after receiving event. Executing initCarouselIE2.");
//     initCarouselIE2();
//   }
// }
function addSkeleton(createHeader, panelsContainer, createFooter) {
    console.log("addSkeleton START ");
    //globalInteractiveElements=carouselElements;
    //Add skeleton class to header
    globalInteractiveElements.push(createHeader);
    //Add skeleton class to panels container
    panelsContainer.classList.add("kmd-skeleton");
    globalInteractiveElements.push(panelsContainer);
    //Add skeleton class to footer
    createFooter.classList.add("kmd-skeleton");
    globalInteractiveElements.push(createFooter);
}
function removeSkeleton() {
    for(var i = 0; i < globalInteractiveElements.length; i++){
        globalInteractiveElements[i].classList.remove("kmd-skeleton");
    }
    globalInteractiveElements = [];
//Remove skeleton class to header
//createHeader.classList.remove("kmd-skeleton");
//Remove skeleton class to panels container
//panelsContainer.classList.remove("kmd-skeleton");
//Remove skeleton class to footer
//createFooter.classList.remove("kmd-skeleton");
}
function validateCarousels() {
    var kmdCarousels = document.querySelectorAll(".kmd-carousel");
    var carouselElements = [];
    for(var i = 0; i < kmdCarousels.length; i++){
        var kmdCarousel = kmdCarousels[i];
        if (!kmdCarousel) {
            console.error("Carousel element not found.");
            continue;
        }
        var createHeader = kmdCarousel.querySelector(".kmd-carousel__header");
        var panelsContainer = kmdCarousel.querySelector(".kmd-carousel__panels-container");
        var createFooter = kmdCarousel.querySelector(".kmd-carousel__footer");
        if (!panelsContainer || !createFooter) {
            console.error("Required child elements missing in the carousel.");
            continue;
        }
        carouselElements.push({
            kmdCarousel: kmdCarousel,
            createHeader: createHeader,
            panelsContainer: panelsContainer,
            createFooter: createFooter
        });
    }
    console.log("initializeCarousels " + carouselElements);
    return carouselElements;
}
function supportsAllFeatures() {
    return 'Promise' in window && 'Symbol' in window && 'classList' in document.createElement('p') && 'flex' in document.documentElement.style && typeof Object.assign != "undefined";
}
function initCarouselIE() {
    // Selecting carousel elements
    var kmdCarousel = document.querySelector(".kmd-carousel");
    if (!kmdCarousel) {
        return;
    } else {
        var getActiveItemIndexes = // Function to get active item indexes based on current page
        function getActiveItemIndexes() {
            var activeIndexes = [];
            var startIndex1 = index * itemsPerPage;
            var endIndex = startIndex1 + itemsPerPage;
            for(var i = 0; i < items.length; i++){
                if (i >= startIndex1 && i < endIndex) {
                    activeIndexes.push(i);
                }
            }
            return activeIndexes;
        };
        var updatePaginationLabel = // Function to update pagination label with current page details
        function updatePaginationLabel(startIndex1, endIndex, itemsTotal) {
            PAGINATION_LABEL = '<div class="kmd-carousel__pagination-label">Viewing <strong> $startIndex - $endIndex </strong>of<strong> $itemsTotal </strong></div>';
            var parts = PAGINATION_LABEL.split('$startIndex');
            var updatedLabel = parts[0] + startIndex1 + parts[1].split('$endIndex')[0] + endIndex + parts[1].split('$endIndex')[1].split('$itemsTotal')[0] + itemsTotal + parts[1].split('$endIndex')[1].split('$itemsTotal')[1];
            paginationLabel.innerHTML = updatedLabel;
        };
        var updateLabelText1 = // Function to update pagination label text based on current page
        function updateLabelText1() {
            var activeItems = getActiveItemIndexes();
            startIndex = index * itemsPerPage + 1;
            if (getLastPage1() == true) {
                startIndex = itemsTotal - itemsPerPage + 1;
            }
            endIndex = Math.min(startIndex + itemsPerPage - 1, itemsTotal);
            updatePaginationLabel(startIndex, endIndex, itemsTotal);
        };
        var getLastPage1 = // Function to check if current page is the last page
        function getLastPage1() {
            transformValue = index * -(width * itemsPerPage);
            if (parseInt(itemExtras) !== itemExtras) {
                extraValue = decimal * 100;
                pageCount = parseInt(itemExtras) * 100;
                currentPage = index * (width * itemsPerPage);
                if (currentPage == pageCount) {
                    return true;
                } else {
                    return false;
                }
            }
        };
        var getVisiblePanels = // Function to get visible panels based on carousel classes
        function getVisiblePanels() {
            var panelsContainerClasses = panelsContainer.className.split(' ');
            breakpoints = {
                xs: null,
                sm: null,
                md: null,
                lg: null,
                xl: null,
                hd: null
            };
            panelesPorBreakpoint = {
                xs: 1,
                sm: 1,
                md: 1,
                lg: 1,
                xl: 1,
                hd: 1
            };
            for(var i = 0; i < panelsContainerClasses.length; i++){
                var clase = panelsContainerClasses[i];
                if (clase.indexOf('kmd-carousel__visible-panels-') !== -1) {
                    var breakpoint = clase.split('kmd-carousel__visible-panels-')[1];
                    var bpName = breakpoint.split('-')[0];
                    var numPaneles = parseInt(breakpoint.split('-')[1], 10);
                    panelesPorBreakpoint[bpName] = numPaneles;
                }
            }
            breakpoints.xs = panelesPorBreakpoint.xs;
            breakpoints.sm = panelesPorBreakpoint.sm;
            breakpoints.md = panelesPorBreakpoint.md;
            breakpoints.lg = panelesPorBreakpoint.lg;
            breakpoints.xl = panelesPorBreakpoint.xl;
            breakpoints.hd = panelesPorBreakpoint.hd;
            return breakpoints;
        };
        var onWindowResize1 = // Function to update visible panels on window resize
        function onWindowResize1() {
            getVisiblePanels();
            var xs = 391;
            var sm = 577;
            var md = 769;
            var lg = 993;
            var xl = 1201;
            //   var hd = 1400;
            var windowWidth = window.innerWidth;
            if (windowWidth < xs) {
                return breakpoints['xs'];
            } else if (windowWidth >= xs && windowWidth < sm) {
                return breakpoints['sm'];
            } else if (windowWidth >= sm && windowWidth < md) {
                return breakpoints['md'];
            } else if (windowWidth >= md && windowWidth < lg) {
                return breakpoints['lg'];
            } else if (windowWidth >= lg && windowWidth < xl) {
                return breakpoints['xl'];
            } else if (windowWidth >= xl) {
                return breakpoints['hd'];
            } else {
                return breakpoints['hd'];
            }
        //   updateCarousel()
        //   return;
        };
        var updateCarousel1 = function updateCarousel1() {
            itemsPerPage = onWindowResize1();
            scrollPercentage = 100 / (itemsTotal / itemsPerPage);
            width = 100 / itemsPerPage;
            scrollWidth = scrollPercentage;
            index = 0;
            previousArrow.setAttribute("disabled", "disabled");
            nextArrow.removeAttribute("disabled");
            indicatorBar.style.width = scrollWidth + "%";
            indicatorBar.style.transform = "translateX(0%)";
            panelsContainerTransform.style.transform = "translateX(0%)";
            itemExtras = itemsTotal / itemsPerPage;
            decimal = itemExtras - Math.floor(itemExtras);
            updateLabelText1();
        };
        var panelsContainer = document.querySelector(".kmd-carousel__panels-container");
        // Variables for breakpoints and pagination
        var panelesPorBreakpoint;
        var breakpoints;
        var itemsPerPage = onWindowResize1(); // Calculate items per page based on window size
        var transformValue, extraValue, pageCount, currentPage; // Variables for transformation values and pagination logic
        var panelesVisibles; // Visible panels variable
        var items = panelsContainer.children; // Get all child items of panels container
        var itemsTotal = items.length; // Total number of items in the carousel
        // Variables for pagination and scroll indicators
        var itemExtras = itemsTotal / itemsPerPage;
        var decimal = itemExtras - Math.floor(itemExtras);
        var width = 100 / itemsPerPage;
        var index = 0;
        var endIndex;
        var scrollPercentage = 100 / (itemsTotal / itemsPerPage);
        var scrollValue = scrollPercentage;
        var scrollLastValue;
        var scrollWidth = scrollPercentage;
        var scrollLastWidth = 100 - scrollWidth * decimal;
        /////////////
        // Creating footer and pagination elements
        var createFooter = document.querySelector(".kmd-carousel__footer");
        var PAGINATION_LABEL = '<div class="kmd-carousel__pagination-label">Viewing <strong> $startIndex</strong>  <span class="kmd-carousel__pagination-label-endindex"><strong>- $endIndex</strong></span> of<strong> $itemsTotal</strong></div>';
        var PAGINATION_ARROWS = '<div class="kmd-carousel__pagination">' + '<span class="kmd-carousel__pagination-previous-arrow" role="button" tabindex="0" disabled="disabled">' + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' + '<path fill-rule="evenodd" clip-rule="evenodd" d="M17.7 3.42L16.29 2L6.29004 12L16.29 22L17.7 20.59L9.11004 12L17.7 3.42Z" fill="#1B1B1D"/>' + '</svg>' + '</span>' + '<span class="kmd-carousel__pagination-next-arrow" role="button" tabindex="0">' + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' + '<path fill-rule="evenodd" clip-rule="evenodd" d="M6.29004 3.42L7.70004 2L17.7 12L7.70004 22L6.29004 20.59L14.87 12L6.29004 3.42Z" fill="#1B1B1D"/>' + '</svg>' + '</span></div>';
        // Adding pagination elements to the footer
        createFooter.innerHTML = PAGINATION_LABEL + PAGINATION_ARROWS;
        // Adding scroll indicator to the carousel
        var SCROLL_INDICATOR = '<div class="kmd-carousel__scroll-indicator"><span class="kmd-carousel__scroll-indicator-bar"></span></div>';
        kmdCarousel.innerHTML += SCROLL_INDICATOR;
        var scrollIndicator = document.querySelector(".kmd-carousel__scroll-indicator");
        /////////////
        // Selecting elements for interaction
        var indicatorBar = document.querySelector(".kmd-carousel__scroll-indicator-bar");
        var previousArrow = document.querySelector(".kmd-carousel__pagination-previous-arrow");
        var nextArrow = document.querySelector(".kmd-carousel__pagination-next-arrow");
        var panelsContainerTransform = document.querySelector('.kmd-carousel__panels-container');
        var paginationLabel = document.querySelector(".kmd-carousel__pagination-label");
        // Disabling previous arrow initially
        previousArrow.setAttribute("disabled", "disabled");
        //////////////////////
        // Event listener for window resize
        window.addEventListener("resize", function() {
            width = kmdCarousel.offsetWidth; // Update width on window resize
        });
        // Initial styling for scroll indicator
        indicatorBar.style.transform = "translateX(" + 0 + "%)";
        indicatorBar.style.width = scrollWidth + "%";
        //Hide elements
        if (itemsTotal <= itemsPerPage) {
            nextArrow.style.display = "none";
            previousArrow.style.display = "none";
            scrollIndicator.style.display = "none";
            paginationLabel.style.display = "none";
        }
        // Event listener for next arrow click
        nextArrow.addEventListener("click", function(e) {
            e.preventDefault();
            if (index == 0) {
                scrollValue = scrollPercentage;
            }
            index = index + 1;
            previousArrow.removeAttribute("disabled");
            if (getLastPage1() == true) {
                transformValue = -(currentPage - 100 + extraValue);
                scrollLastValue = scrollValue;
            }
            panelsContainerTransform.style.transform = "translateX(" + transformValue + "%)";
            scrollValue = scrollValue; //scrollValue + scrollPercentage;
            if (scrollValue > 100) {
                scrollValue = scrollValue - 100 + extraValue;
                //scrollValue = 100 + extraValue;
                //indicatorBar.style.width = scrollLastWidth + "%";
                indicatorBar.style.transform = "translateX(" + scrollValue + "%)";
            } else {
                indicatorBar.style.transform = "translateX(" + scrollValue + "%)";
                indicatorBar.style.width = scrollWidth + "%";
            }
            if (itemsTotal <= endIndex + itemsPerPage) {
                nextArrow.setAttribute("disabled", "disabled");
            }
            updateLabelText1();
        });
        // Event listener for previous arrow click
        previousArrow.addEventListener("click", function() {
            index = index - 1;
            nextArrow.removeAttribute("disabled");
            if (index === 0) {
                previousArrow.setAttribute("disabled", "disabled");
            }
            if (scrollValue == 0) {
                if (typeof extraValue !== "undefined") {
                    if (itemsTotal % 2 === 0) {
                        scrollValue = scrollPercentage;
                    } else {
                        scrollValue = scrollLastValue;
                    }
                } else {
                    scrollValue = scrollValue - scrollPercentage;
                }
                indicatorBar.style.width = scrollWidth + "%";
            } else if (index == 0) {
                scrollValue = 0;
                indicatorBar.style.transform = "translateX(" + scrollValue + "%)";
                indicatorBar.style.width = scrollWidth + "%";
            } else {
                scrollValue = index * (width * itemsPerPage);
                //scrollValue = scrollValue - scrollPercentage;
                indicatorBar.style.transform = "translateX(" + scrollValue + "%)";
                indicatorBar.style.width = scrollWidth + "%";
            }
            panelsContainerTransform.style.transform = "translateX(" + index * -(width * itemsPerPage) + "%)";
            updateLabelText1();
        });
        // Initial update of pagination label text
        updateLabelText1();
        // Event listeners for window resize
        window.onresize = onWindowResize1;
        window.addEventListener('DOMContentLoaded', onWindowResize1);
        window.addEventListener('resize', onWindowResize1);
        window.addEventListener("resize", updateCarousel1);
    ///
    ////
    }
}
function initCarouselIE2() {
    var _loop = function() {
        var updateLabelText1 = /*
            function updatePaginationLabel(startIndex, values, itemsTotal, interactiveElements) {
                var PAGINATION_LABEL = '<div class="kmd-carousel__pagination-label">Viewing <strong> $startIndex - $endIndex </strong>of<span class="kmd-carousel__pagination-label-totalitems"><strong> $itemsTotal </strong></span><span class="kmd-carousel__pagination-label-mobile"> Products</span></div>';
                var firstReplace = PAGINATION_LABEL.replace('$startIndex', startIndex);
                var endIndexPart = firstReplace.split('$endIndex');
                var beforeEndIndex = endIndexPart[0];
                var afterEndIndex = endIndexPart[1].replace('$itemsTotal', itemsTotal);


                var updatedLabel = beforeEndIndex + values.endIndex + afterEndIndex;

                interactiveElements.paginationLabel.innerHTML = updatedLabel;

                // Update cards tabindex
                updateCardsTabindex(interactiveElements, values, startIndex);
            }
    */ function updateLabelText1(values, itemsPerPage, itemsTotal, interactiveElements) {
            var startIndex1 = values.index * itemsPerPage + 1;
            if (getLastPage1(values, itemsPerPage)) {
                startIndex1 = itemsTotal - itemsPerPage + 1;
            }
            values.endIndex = Math.min(startIndex1 + itemsPerPage - 1, itemsTotal);
            // Update cards tabindex
            updateCardsTabindex(interactiveElements, values, startIndex1);
        //updatePaginationLabel(startIndex, values, itemsTotal, interactiveElements);
        };
        var updateCardsTabindex = function updateCardsTabindex(interactiveElements, values, startIndex1) {
            // Get all cards
            var cards = interactiveElements.panelsContainerTransform.children;
            for(var i = 0; i < cards.length; i++){
                var card = cards[i];
                if (i >= startIndex1 - 1 && i < values.endIndex) {
                    setTabindexForCardElements(card, '0');
                } else {
                    setTabindexForCardElements(card, '-1');
                }
            }
        };
        var setTabindexForCardElements = function setTabindexForCardElements(card, tabindexValue) {
            var focusableElements = card.querySelectorAll('a, button, input, select, textarea, [tabindex]');
            for(var i = 0; i < focusableElements.length; i++){
                var element = focusableElements[i];
                element.setAttribute('tabindex', tabindexValue);
            }
        };
        var getLastPage1 = function getLastPage1(values, itemsPerPage) {
            values.transformValue = values.index * -(values.width * itemsPerPage);
            if (parseInt(values.itemExtras) !== values.itemExtras) {
                values.extraValue = values.decimal * 100;
                values.pageCount = parseInt(values.itemExtras) * 100;
                values.currentPage = values.index * (values.width * itemsPerPage);
                if (values.currentPage == values.pageCount) {
                    return true;
                } else {
                    return false;
                }
            }
        };
        var getVisiblePanels = function getVisiblePanels(carousel) {
            var panelsContainer = carousel.panelsContainer;
            var panelsContainerClasses = panelsContainer.className.split(' ');
            var panelesPerBreakpoint = {
                xs: 0,
                sm: 0,
                md: 0,
                lg: 0,
                xl: 0,
                hd: 0
            };
            var bpArray = [];
            var sizes = [
                'xs',
                'sm',
                'md',
                'lg',
                'xl',
                'hd'
            ];
            var defaultSize = 0;
            for(var i = 0; i < panelsContainerClasses.length; i++){
                var clase = panelsContainerClasses[i];
                if (clase.indexOf('kmd-carousel__visible-panels-') !== -1) {
                    var breakpoint = clase.split('kmd-carousel__visible-panels-')[1];
                    var bpName = breakpoint.split('-')[0];
                    var numPaneles = parseInt(breakpoint.split('-')[1], 10);
                    if (typeof bpName === 'string' && isNaN(parseFloat(bpName))) {
                        bpArray.push(bpName);
                    } else {
                        defaultSize = bpName;
                    }
                    panelesPerBreakpoint[bpName] = numPaneles;
                }
            }
            breakpoints.xs = panelesPerBreakpoint.xs;
            breakpoints.sm = panelesPerBreakpoint.sm;
            breakpoints.md = panelesPerBreakpoint.md;
            breakpoints.lg = panelesPerBreakpoint.lg;
            breakpoints.xl = panelesPerBreakpoint.xl;
            breakpoints.hd = panelesPerBreakpoint.hd;
            setMissingSizes(bpArray, sizes, defaultSize, breakpoints, panelesPerBreakpoint);
            return breakpoints;
        };
        var setMissingSizes = //validate the missing sizes classes
        function setMissingSizes(bpArray, sizes, defaultSize, breakpoints, panelesPerBreakpoint) {
            var sizesFaltantes = [];
            var sizeToBreakpointMap = {
                xs: [
                    'sm',
                    'md',
                    'lg',
                    'xl',
                    'hd'
                ],
                sm: [
                    'xs',
                    'md',
                    'lg',
                    'xl',
                    'hd'
                ],
                md: [
                    'sm',
                    'xs',
                    'lg',
                    'xl',
                    'hd'
                ],
                lg: [
                    'md',
                    'sm',
                    'xs',
                    'xl',
                    'hd'
                ],
                xl: [
                    'lg',
                    'md',
                    'sm',
                    'xs',
                    'hd'
                ],
                hd: [
                    'xl',
                    'lg',
                    'md',
                    'sm',
                    'xs'
                ]
            };
            for(var i = 0; i < sizes.length; i++){
                var size = sizes[i];
                if (bpArray.indexOf(size) === -1) {
                    sizesFaltantes.push(size);
                    var availableSizes = sizeToBreakpointMap[size];
                    var breakpoint = 'defaultSize'; //default size
                    // Find the first available size
                    for(var j = 0; j < availableSizes.length; j++){
                        var availableSize = availableSizes[j];
                        if (panelesPerBreakpoint[availableSize] !== 0) {
                            breakpoint = availableSize;
                            break;
                        }
                    }
                    breakpoints[size] = panelesPerBreakpoint[breakpoint] || defaultSize; // Use the default size if no other size is available
                }
            }
        };
        var setXsClassValue = function setXsClassValue(carousel) {
            var classes = carousel.panelsContainer.className.split(/\s+/);
            for(var i = 0; i < classes.length; i++){
                var className = classes[i];
                if (className.indexOf("kmd-carousel__visible-panels-") === 0) {
                    var sizeValue = parseInt(className.replace("kmd-carousel__visible-panels-", ""), 10);
                    carousel.panelsContainer.classList.add("kmd-carousel__visible-panels-xs-" + sizeValue);
                    break;
                }
            }
        };
        var onWindowResize1 = //Arrows
        function onWindowResize1(carousel, func) {
            getVisiblePanels(carousel);
            var xs = 391;
            var sm = 577;
            var md = 769;
            var lg = 993;
            var xl = 1201;
            var hd = 1400;
            var windowWidth = window.innerWidth;
            if (windowWidth < xs) {
                return breakpoints['xs'];
            } else if (windowWidth >= xs && windowWidth < sm) {
                return breakpoints['sm'];
            } else if (windowWidth >= sm && windowWidth < md) {
                return breakpoints['md'];
            } else if (windowWidth >= md && windowWidth < lg) {
                return breakpoints['lg'];
            } else if (windowWidth >= lg && windowWidth < xl) {
                return breakpoints['xl'];
            } else if (windowWidth >= xl) {
                return breakpoints['hd'];
            } else {
                return breakpoints['hd'];
            }
        };
        var updateCarouselElements = function updateCarouselElements(carousel, interactiveElements, values, itemsTotal, itemsPerPage) {
            if (swipeScroll == 0) {
                var previousArrow = interactiveElements.previousArrow;
                //			var indicatorBar = interactiveElements.indicatorBar;
                //			var scrollIndicator = interactiveElements.scrollIndicator;
                var nextArrow = interactiveElements.nextArrow;
                var paginationLabel = interactiveElements.paginationLabel;
                var footerSection = interactiveElements.footerSection;
                //if (swipeScroll==0){
                itemsPerPage = onWindowResize1(carousel, "updateCarouselElements");
                values.itemWidthValue = 0;
                //}
                /*
        if (previousArrow) {
            alert("updateCarouselElements index: " + values.index);
            previousArrow.setAttribute("disabled", "disabled");
        } else {
            console.error("Previous arrow element is missing in ", carousel.kmdCarousel);
        }*/ if (itemsTotal <= itemsPerPage) {
                    nextArrow.style.visibility = "hidden";
                    previousArrow.style.visibility = "hidden";
                    paginationLabel.style.visibility = "hidden";
                    footerSection.style.display = "none";
                } else {
                    nextArrow.style.visibility = "visible";
                    previousArrow.style.visibility = "visible";
                    paginationLabel.style.visibility = "visible";
                    footerSection.style.display = "flex";
                }
            /*
                    if (indicatorBar) {
                        indicatorBar.style.transform = "translateX(0%)";
                        indicatorBar.style.width = values.scrollWidth + "%";
                        values.itemWidthValue = 0;


                        if (itemsTotal <= itemsPerPage) {
                            nextArrow.style.visibility = "hidden";
                            previousArrow.style.visibility = "hidden";
                            scrollIndicator.style.visibility = "hidden";
                            paginationLabel.style.visibility = "hidden";

                        }else{
                            nextArrow.style.visibility = "visible";
                            previousArrow.style.visibility = "visible";
                            scrollIndicator.style.visibility = "visible";
                            paginationLabel.style.visibility = "visible";
                        }

                    } else {
                        console.error("Indicator bar element is missing in ", carousel.kmdCarousel);
                    }
        */ }
        };
        var updateCarousel1 = function updateCarousel1(interactiveElements, values, itemsPerPage, itemsTotal, carousel) {
            console.log("updateCarousel");
            if (swipeScroll == 1) {
                // alert("updateCarousel swipeScroll: " + swipeScroll);
                return;
            }
            if (swipeScroll == 0) {
                // alert("updateCarousel swipeScroll: " + swipeScroll);
                var previousArrow = interactiveElements.previousArrow;
                var nextArrow = interactiveElements.nextArrow;
                //var indicatorBar = interactiveElements.indicatorBar;
                var panelsContainerTransform = interactiveElements.panelsContainerTransform;
                /*
            values.scrollPercentage = 100 / (itemsTotal / itemsPerPage);
        */ values.width = 100 / itemsPerPage;
                values.scrollWidth = values.scrollPercentage;
                if (interactiveElements) {
                    //	indicatorBar.style.width = values.scrollWidth + "%";
                    //	indicatorBar.style.transform = "translateX(0%)";
                    values.index = 0;
                    previousArrow.setAttribute("disabled", "disabled");
                    nextArrow.removeAttribute("disabled");
                    panelsContainerTransform.style.transform = "translateX(0%)";
                }
                values.itemExtras = itemsTotal / itemsPerPage;
                values.decimal = values.itemExtras - Math.floor(values.itemExtras);
                updateLabelText1(values, itemsPerPage, itemsTotal, interactiveElements);
                updateCarouselElements(carousel, interactiveElements, values, itemsTotal, itemsPerPage);
            }
        };
        var carousel = carousels[i];
        setXsClassValue(carousel);
        var breakpoints = {
            xs: null,
            sm: null,
            md: null,
            lg: null,
            xl: null,
            hd: null
        };
        var itemsPerPage = onWindowResize1(carousel, "initCarouselIE2");
        var items = carousel.panelsContainer.children;
        var itemsTotal = items.length;
        var languagetxt = "Products";
        var values = {
            transformValue: 0,
            extraValue: 0,
            pageCount: 0,
            currentPage: 0,
            itemExtras: itemsTotal / itemsPerPage,
            decimal: itemsTotal / itemsPerPage - Math.floor(itemsTotal / itemsPerPage),
            width: 100 / itemsPerPage,
            index: 0,
            endIndex: 0,
            scrollPercentage: 100 / (itemsTotal / itemsPerPage),
            scrollValue: 100 / (itemsTotal / itemsPerPage),
            scrollLastValue: 0,
            scrollWidth: 100 / (itemsTotal / itemsPerPage),
            itemWidth: 0,
            itemWidthValue: 0,
            itemCurrentPage: 0,
            itemLastValue: 0
        };
        var startX, endX;
        createCarouselElements(carousel, itemsPerPage, itemsTotal, languagetxt);
        offsetScreenWidth = carousel.kmdCarousel.offsetWidth; //Get actual size of the carousel
        var interactiveElements = getInteractiveElements(carousel.kmdCarousel);
        if (!interactiveElements) {
            console.error("Failed to initialize interactive elements for carousel: ", carousel.kmdCarousel);
            return "continue";
        }
        //HEREEEEEEE
        setupEventListeners(values, interactiveElements, itemsPerPage, itemsTotal, carousel);
        // //swipe
        setupSwipeListeners(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
        // //end swipe
        setupScrollListeners(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
        updateLabelText1(values, itemsPerPage, itemsTotal, interactiveElements);
        updateCarousel1(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
        updateCarouselElements(carousel, interactiveElements, values, itemsTotal, itemsPerPage);
    };
    // Main flow
    var carousels = initializeCarousels();
    if (!carousels.length) {
        console.error("No carousel elements found.");
        return;
    }
    for(var i = 0; i < carousels.length; i++)_loop();
     //end for
    //1
    function initializeCarousels() {
        var kmdCarousels = document.querySelectorAll(".kmd-carousel");
        var carouselElements = [];
        for(var i = 0; i < kmdCarousels.length; i++){
            var kmdCarousel = kmdCarousels[i];
            if (!kmdCarousel) {
                console.error("Carousel element not found.");
                continue;
            }
            var createHeader = kmdCarousel.querySelector(".kmd-carousel__header");
            var panelsContainer = kmdCarousel.querySelector(".kmd-carousel__panels-container");
            var createFooter = kmdCarousel.querySelector(".kmd-carousel__footer");
            if (!panelsContainer || !createFooter) {
                console.error("Required child elements missing in the carousel.");
                continue;
            }
            carouselElements.push({
                kmdCarousel: kmdCarousel,
                createHeader: createHeader,
                panelsContainer: panelsContainer,
                createFooter: createFooter
            });
            console.log("initializeCarousels");
        //removeSkeleton(createHeader,panelsContainer,createFooter);
        }
        console.log("initializeCarousels", carouselElements);
        return carouselElements;
    }
    function createCarouselElements(carousel, itemsPerPage, itemsTotal, languagetxt) {
        console.log("createCarouselElements");
        updateLanguageLabel(languagetxt);
        //		var kmdCarousel = carousel.kmdCarousel;
        var createFooter = carousel.createFooter;
        //		var PAGINATION_LABEL = '<div class="kmd-carousel__pagination-label">Viewing <strong>' + 1 + ' - ' + itemsPerPage + '</strong> of <span class="kmd-carousel__pagination-label-totalitems"><strong>' + itemsTotal + '</strong></span><span class="kmd-carousel__pagination-label-mobile"> Products</span></div>';
        var PAGINATION_LABEL = '<div class="kmd-carousel__pagination-label">' + itemsTotal + ' <span> ' + languagetxt + '</span></div>';
        var PAGINATION_ARROWS = '<div class="kmd-carousel__pagination">' + '<span class="kmd-carousel__pagination-previous-arrow" role="button" aria-label="Previous" tabindex="0" disabled="disabled">' + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' + '<path fill-rule="evenodd" clip-rule="evenodd" d="M17.7 3.42L16.29 2L6.29004 12L16.29 22L17.7 20.59L9.11004 12L17.7 3.42Z" fill="#1B1B1D"/>' + '</svg>' + '</span>' + '<span class="kmd-carousel__pagination-next-arrow" role="button" aria-label="Next" tabindex="0">' + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' + '<path fill-rule="evenodd" clip-rule="evenodd" d="M6.29004 3.42L7.70004 2L17.7 12L7.70004 22L6.29004 20.59L14.87 12L6.29004 3.42Z" fill="#1B1B1D"/>' + '</svg>' + '</span>' + '</div>';
        createFooter.innerHTML = PAGINATION_LABEL + PAGINATION_ARROWS;
    //	var SCROLL_INDICATOR = '<div class="kmd-carousel__scroll-indicator"><span class="kmd-carousel__scroll-indicator-bar"></span></div>';
    //	kmdCarousel.innerHTML += SCROLL_INDICATOR;
    }
    //3
    function getInteractiveElements(carousel) {
        //		var scrollIndicator = carousel.querySelector(".kmd-carousel__scroll-indicator");
        //		var indicatorBar = carousel.querySelector(".kmd-carousel__scroll-indicator-bar");
        var previousArrow = carousel.querySelector(".kmd-carousel__pagination-previous-arrow");
        var nextArrow = carousel.querySelector(".kmd-carousel__pagination-next-arrow");
        var panelsContainerTransform = carousel.querySelector('.kmd-carousel__panels-container');
        var paginationLabel = carousel.querySelector(".kmd-carousel__pagination-label");
        var footerSection = carousel.querySelector(".kmd-carousel__footer");
        var headerSection = carousel.querySelector(".kmd-carousel__header");
        if (/*!indicatorBar ||*/ !previousArrow || !nextArrow || !panelsContainerTransform || !paginationLabel || !footerSection || !headerSection) {
            console.error("One or more interactive elements are missing in the carousel.");
            return null;
        }
        return {
            //			scrollIndicator: scrollIndicator,
            //			indicatorBar: indicatorBar,
            previousArrow: previousArrow,
            nextArrow: nextArrow,
            panelsContainerTransform: panelsContainerTransform,
            paginationLabel: paginationLabel,
            footerSection: footerSection,
            headerSection: headerSection
        };
    }
    function setupEventListeners(values, interactiveElements, itemsPerPage, itemsTotal, carousel) {
        // window.addEventListener("resize", function () {
        // 	itemsPerPage = onWindowResize(carousel,"setupEventListeners");
        // 	updateCarousel(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
        // });
        window.addEventListener("resize", handleResize);
        window.addEventListener("orientationchange", handleOrientationChange);
        function handleResize() {
            if (swipeScroll == 1) {
                return;
            }
            itemsPerPage = onWindowResize(carousel);
            if (typeof offsetScreenWidth !== 'undefined' && offsetScreenWidth !== carousel.kmdCarousel.offsetWidth) {
                swipeScroll = 0;
                offsetScreenWidth = carousel.kmdCarousel.offsetWidth;
            }
            updateCarousel(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
            accumulatedDelta = 0;
        }
        function handleOrientationChange() {
            // alert("handleOrientationChange");
            swipeScroll = 0;
            handleResize();
        }
        interactiveElements.nextArrow.addEventListener("click", function(e) {
            e.preventDefault();
            handleNextClick(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
        });
        interactiveElements.previousArrow.addEventListener("click", function(e) {
            e.preventDefault();
            handlePreviousClick(interactiveElements, values, itemsPerPage, itemsTotal);
        });
        interactiveElements.nextArrow.addEventListener("keydown", function(e) {
            if ((e.key === "Enter" || e.key === " ") && !interactiveElements.nextArrow.hasAttribute('disabled')) {
                e.preventDefault();
                interactiveElements.nextArrow.click();
            }
        });
        interactiveElements.previousArrow.addEventListener("keydown", function(e) {
            if ((e.key === "Enter" || e.key === " ") && !interactiveElements.previousArrow.hasAttribute('disabled')) {
                e.preventDefault();
                interactiveElements.previousArrow.click();
            }
        });
    }
    //swipe
    function setupSwipeListeners(interactiveElements, values, itemsPerPage, itemsTotal, carousel) {
        // window.addEventListener("resize", function () {
        // 	if(offsetScreenWidth != carousel.kmdCarousel.offsetWidth){
        // 		swipeScroll = 0;
        // 		itemsPerPage = onWindowResize(carousel,"setupSwipeListeners");
        // 		updateCarousel(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
        // 		offsetScreenWidth = carousel.kmdCarousel.offsetWidth;//Get actual size of the carousel
        // 	}
        // });
        //var startX, endX;
        function handleTouchStart(event) {
            startX = event.touches[0].clientX;
            endX = startX;
        }
        function handleTouchMove(event) {
            endX = event.touches[0].clientX;
        //event.preventDefault();
        }
        function handleTouchEnd() {
            var diffX = endX - startX;
            if (Math.abs(diffX) > 50) {
                if (diffX > 0) {
                    handlePreviousClick(interactiveElements, values, itemsPerPage, itemsTotal);
                    diffX = 0;
                } else {
                    handleNextClick(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
                    diffX = 0;
                }
            }
        }
        interactiveElements.panelsContainerTransform.addEventListener('touchstart', handleTouchStart, {
            passive: true
        });
        interactiveElements.panelsContainerTransform.addEventListener('touchmove', handleTouchMove, {
            passive: false
        });
        interactiveElements.panelsContainerTransform.addEventListener('touchend', handleTouchEnd, {
            passive: true
        });
    }
    //end swipe
    function setupScrollListeners(interactiveElements, values, itemsPerPage, itemsTotal, carousel) {
        // window.addEventListener("resize", function () {
        // 	itemsPerPage = onWindowResize(carousel,"setupScrollListeners");
        // 	updateCarousel(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
        // 	accumulatedDelta = 0;
        // });
        var panelsContainer = interactiveElements.panelsContainerTransform;
        var isScrolling = false;
        panelsContainer.addEventListener('wheel', function(event) {
            //event = event || window.event;
            var deltaX = event.deltaX || event.wheelDeltaX || 0;
            //var deltaY = event.deltaY || event.wheelDeltaY || event.wheelDelta || 0;
            if (isScrolling) return;
            isScrolling = true;
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                if (event.deltaX > 0) {
                    handleNextClick(interactiveElements, values, itemsPerPage, itemsTotal, carousel);
                } else {
                    handlePreviousClick(interactiveElements, values, itemsPerPage, itemsTotal);
                }
            }
            // Delay resetting isScrolling to allow one move at a time
            setTimeout(function() {
                isScrolling = false;
            }, 500); // Adjust the delax as necessary
        }, {
            passive: true
        });
    }
    //5
    function handleNextClick(interactiveElements, values, itemsPerPage, itemsTotal, carousel) {
        if (itemsTotal != values.endIndex) {
            values.index = values.index + 1;
            carousel = carousel.kmdCarousel;
            values.itemWidth = carousel.querySelector(".kmd-carousel__item").offsetWidth;
            var previousArrow = interactiveElements.previousArrow;
            var panelsContainerTransform = interactiveElements.panelsContainerTransform;
            //		var indicatorBar = interactiveElements.indicatorBar;
            var nextArrow = interactiveElements.nextArrow;
            previousArrow.removeAttribute("disabled");
            if (getLastPage(values, itemsPerPage) == true) {
                values.transformValue = -(values.currentPage - 100 + values.extraValue);
                values.itemLastValue = -(values.itemWidth + 22) * (itemsTotal - itemsPerPage);
                panelsContainerTransform.style.transform = "translateX(" + values.itemLastValue + "px)";
            //
            } else {
                values.itemLastValue = 0;
            }
            //pixel change start
            if (values.index == 0) {
                values.itemWidthValue = -(values.itemWidth + 22) * itemsPerPage;
            } else {
                values.itemWidthValue = values.itemWidthValue + -(values.itemWidth + 22) * itemsPerPage;
            }
            if (values.itemLastValue == 0) {
                panelsContainerTransform.style.transform = "translateX(" + values.itemWidthValue + "px)";
            }
            //pixel change ends
            /*
              values.scrollValue = -(values.transformValue);

              if (values.scrollValue > 100) {
                values.scrollValue = 100;//(values.scrollValue - 100 + values.extraValue)

                values.scrollValue =  -(values.transformValue);

                indicatorBar.style.transform = "translateX(" + values.scrollValue + "%)";
              } else {

                  indicatorBar.style.transform = "translateX(" + values.scrollValue  + "%)";

                  indicatorBar.style.width = values.scrollWidth + "%";
              }
      */ if (itemsTotal <= values.endIndex + itemsPerPage) {
                nextArrow.setAttribute("disabled", "disabled");
            }
            updateLabelText(values, itemsPerPage, itemsTotal, interactiveElements);
        }
    }
    function handlePreviousClick(interactiveElements, values, itemsPerPage, itemsTotal) {
        if (values.index > 0) {
            var previousArrow = interactiveElements.previousArrow;
            var panelsContainerTransform = interactiveElements.panelsContainerTransform;
            //		var indicatorBar = interactiveElements.indicatorBar;
            var nextArrow = interactiveElements.nextArrow;
            values.index = values.index - 1;
            nextArrow.removeAttribute("disabled");
            if (values.index === 0) {
                previousArrow.setAttribute("disabled", "disabled");
            }
            /*
              if (values.scrollValue == 0) {
                  if (typeof values.extraValue !== "undefined") {
                  if (itemsTotal % 2 === 0) {
                      values.scrollValue = values.scrollPercentage;
                  } else {
                      values.scrollValue = values.scrollLastValue;
                  }
                  } else {
                  values.scrollValue = values.scrollValue - values.scrollPercentage;
                  }

                  indicatorBar.style.width = values.scrollWidth + "%";
              } else if (values.index == 0) {
                  values.scrollValue = 0;
                  indicatorBar.style.transform = "translateX(" + values.scrollValue + "%)";
                  indicatorBar.style.width = values.scrollWidth + "%";
              } else {
                  values.scrollValue = values.index * (values.width * itemsPerPage);
                  indicatorBar.style.transform = "translateX(" + values.scrollValue + "%)";
                  indicatorBar.style.width = values.scrollWidth + "%";
              }
      */ panelsContainerTransform.style.transform = "translateX(" + values.index * -(values.itemWidth + 22) * itemsPerPage + "px)";
            values.itemWidthValue = values.index * -(values.itemWidth + 22) * itemsPerPage;
            updateLabelText(values, itemsPerPage, itemsTotal, interactiveElements);
        }
    }
    function updateLanguageLabel(languagetxt) {
        if (document.documentElement.lang === "fr") {
            languagetxt = "Produits";
        }
    }
//Final
}
function initCarousel() {
    // Class constructor for Komodo Carousel  component
    // Implements Komodo component design pattern
    //
    // @constructor
    // @param {HTMLElement} The element that will be upgraded to be Komodo Carousel
    var KomodoCarousel = function KomodoCarousel(element) {
        this.element_ = element;
        this.carouselPanelsContainer = this.element_.getElementsByClassName(this.cssClasses_.PANELS_CONTAINER)[0];
        this.carouselFooter = this.element_.getElementsByClassName(this.cssClasses_.FOOTER)[0];
        this.slidesPerBreakpoint = this.slidesPerBreakpoint();
        if (this.items > this.slidesPerBreakpoint[componentHandler.currentBreakpoint]) {
            this.createFooter();
            this.createScrollIndicator();
        }
        this.initialize();
    };
    window['KomodoCarousel'] = KomodoCarousel;
    // Store strings for class names defined by this component that are used in Javascript.
    // This allows us to simply change it in one place should we decide to modify at a later date.
    //
    // @enum {string}
    // @private
    KomodoCarousel.prototype.cssClasses_ = {
        PREVIOUS_ARROW: 'kmd-carousel__pagination-previous-arrow',
        NEXT_ARROW: 'kmd-carousel__pagination-next-arrow',
        PANELS_CONTAINER: 'kmd-carousel__panels-container',
        FOOTER: 'kmd-carousel__footer',
        ITEM: 'kmd-carousel__item',
        VISIBILITY_PREFIX: 'kmd-carousel__visible-panels-',
        SCROLL_BAR: 'kmd-carousel__scroll-indicator',
        INDICATOR_BAR: 'kmd-carousel__scroll-indicator-bar',
        PAGINATION_LABEL: 'kmd-carousel__pagination-label'
    };
    // Store strings for placeholders defined by this component that are used in Javascript.
    //
    // @enum {string}
    // @private
    KomodoCarousel.prototype.strings_ = {
        ITEM_INDEX: 'data-kmd-carousel-item-index',
        SCROLL_INDICATOR: '<div class="kmd-carousel__scroll-indicator"><span class="kmd-carousel__scroll-indicator-bar"></span></div>',
        PAGINATION_LABEL: '<div class="kmd-carousel__pagination-label">Viewing <strong>$range</strong> of <strong>$total</strong></div>',
        PAGINATION: '<div class="kmd-carousel__pagination">' + '<span class="kmd-carousel__pagination-previous-arrow" role="button" tabindex="0" disabled="disabled">' + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' + '<path fill-rule="evenodd" clip-rule="evenodd" d="M17.7 3.42L16.29 2L6.29004 12L16.29 22L17.7 20.59L9.11004 12L17.7 3.42Z" fill="#1B1B1D"/>' + '</svg>' + '</span>' + '<span class="kmd-carousel__pagination-next-arrow" role="button" tabindex="0">' + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' + '<path fill-rule="evenodd" clip-rule="evenodd" d="M6.29004 3.42L7.70004 2L17.7 12L7.70004 22L6.29004 20.59L14.87 12L6.29004 3.42Z" fill="#1B1B1D"/>' + '</svg>' + '</span></div>'
    };
    // Create carousel footer
    //
    // @public
    KomodoCarousel.prototype.createFooter = function() {
        var pagination = document.createElement('div');
        pagination.innerHTML = this.strings_.PAGINATION_LABEL + this.strings_.PAGINATION;
        this.carouselFooter.innerHTML = pagination.innerHTML;
        this.paginationLabel = this.carouselFooter.querySelector("." + this.cssClasses_.PAGINATION_LABEL);
        this.nextArrow = this.element_.getElementsByClassName(this.cssClasses_.NEXT_ARROW)[0];
        this.next = this.next.bind(this);
        this.nextArrow.addEventListener('click', this.next);
        this.nextArrow.addEventListener('keydown', this.onKeyDown.bind(this));
        this.previousArrow = this.element_.getElementsByClassName(this.cssClasses_.PREVIOUS_ARROW)[0];
        this.previous = this.previous.bind(this);
        this.previousArrow.addEventListener('click', this.previous);
        this.previousArrow.addEventListener('keydown', this.onKeyDown.bind(this));
    };
    KomodoCarousel.prototype["createFooter"] = KomodoCarousel.prototype.createFooter;
    // Remove the carousel footer
    //
    // @public
    KomodoCarousel.prototype.removeFooterAndScrollbar = function() {
        this.carouselFooter.innerHTML = "";
        this.scrollIndicator.remove();
    };
    KomodoCarousel.prototype["removeFooter"] = KomodoCarousel.prototype.removeFooter;
    // Insert the scroll indicator
    //
    // @public
    KomodoCarousel.prototype.createScrollIndicator = function() {
        var scrollIndicator = document.createElement('div');
        scrollIndicator.innerHTML = this.strings_.SCROLL_INDICATOR;
        this.element_.insertBefore(scrollIndicator.firstChild, this.carouselFooter);
        this.scrollIndicatorBar = this.element_.querySelector("." + this.cssClasses_.INDICATOR_BAR);
        this.scrollIndicator = this.element_.querySelector("." + this.cssClasses_.SCROLL_BAR);
    };
    KomodoCarousel.prototype["createScrollIndicator"] = KomodoCarousel.prototype.createScrollIndicator;
    // Initialize the carousel
    //
    // @public
    KomodoCarousel.prototype.initialize = function() {
        this.items = this.element_.getElementsByClassName(this.cssClasses_.ITEM);
        this.totalItems = this.items.length;
        for(var i = 0; i < this.items.length; i++){
            this.items[i].setAttribute('data-kmd-carousel-item-index', i);
        }
        this.updateSlidesVisibility = this.updateSlidesVisibility.bind(this);
        this.updateSlidesVisibility();
        document.addEventListener("komodoResized", (function() {
            this.updateSlidesVisibility();
            this.element_.dispatchEvent(componentHandler.createEvent('resized.komodo.carousel', {
                startIndex: this.startIndex,
                endIndex: this.endIndex
            }, false, true));
        }).bind(this));
        this.carouselPanelsContainer.addEventListener('keydown', this.onKeyDown.bind(this));
        if (this.items > this.slidesPerBreakpoint[componentHandler.currentBreakpoint]) {
            this.maintainArrowState();
        }
    };
    KomodoCarousel.prototype["initialize"] = KomodoCarousel.prototype.initialize;
    // Next Panel
    //
    // @public
    KomodoCarousel.prototype.next = function(event) {
        event.preventDefault();
        this.startIndex = this.endIndex + 1;
        this.endIndex = this.endIndex + this.slidesPerBreakpoint[componentHandler.currentBreakpoint] > this.totalItems - 1 ? this.totalItems - 1 : this.endIndex + this.slidesPerBreakpoint[componentHandler.currentBreakpoint];
        if (this.endIndex === this.totalItems - 1) {
            this.startIndex = this.endIndex - this.slidesPerBreakpoint[componentHandler.currentBreakpoint] + 1;
        }
        this.items[this.endIndex].scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "end"
        });
        this.maintainArrowState();
        this.element_.dispatchEvent(componentHandler.createEvent('nextSlide.komodo.carousel', {
            startIndex: this.startIndex,
            endIndex: this.endIndex
        }, false, true));
    };
    KomodoCarousel.prototype['next'] = KomodoCarousel.prototype.next;
    // Previous Panel
    //
    // @public
    KomodoCarousel.prototype.previous = function(event) {
        event.preventDefault();
        if (this.startIndex - this.slidesPerBreakpoint[componentHandler.currentBreakpoint] < 0) {
            this.startIndex = 0;
            this.endIndex = this.startIndex + this.slidesPerBreakpoint[componentHandler.currentBreakpoint] - 1;
        } else {
            this.endIndex = this.startIndex - 1;
            this.startIndex = this.endIndex - this.slidesPerBreakpoint[componentHandler.currentBreakpoint] + 1;
        }
        this.items[this.startIndex].scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: this.startIndex === 0 ? "center" : "start"
        });
        this.maintainArrowState();
        this.element_.dispatchEvent(componentHandler.createEvent('previousSlide.komodo.carousel', {
            startIndex: this.startIndex,
            endIndex: this.endIndex
        }, false, true));
    };
    KomodoCarousel.prototype['previous'] = KomodoCarousel.prototype.previous;
    // Calculate visible slides per breakpoint
    //
    // @public
    KomodoCarousel.prototype.slidesPerBreakpoint = function() {
        var breakpoints = {
            xs: null,
            sm: null,
            md: null,
            lg: null,
            xl: null,
            hd: null
        };
        for(var i = 0; i < Object.keys(breakpoints).length; i++){
            var bp = Object.keys(breakpoints)[i];
            var reg = new RegExp(this.cssClasses_.VISIBILITY_PREFIX + (bp === "xs" ? "" : bp + "-") + '[0-9]', 'g');
            if (this.carouselPanelsContainer.classList.value.match(reg)) {
                breakpoints[bp] = parseInt(this.carouselPanelsContainer.classList.value.match(reg)[0].split('-').pop());
            } else {
                breakpoints[bp] = breakpoints[Object.keys(breakpoints)[i - 1]];
            }
        }
        return breakpoints;
    };
    KomodoCarousel.prototype['slidesPerBreakpoint'] = KomodoCarousel.prototype.slidesPerBreakpoint;
    // Maintain arrow state
    //
    // @private
    KomodoCarousel.prototype.maintainArrowState = function() {
        if (this.endIndex === this.items.length - 1) {
            this.nextArrow.setAttribute('disabled', 'disabled');
        } else {
            this.nextArrow.removeAttribute('disabled');
        }
        if (this.startIndex === 0) {
            this.previousArrow.setAttribute('disabled', 'disabled');
        } else {
            this.previousArrow.removeAttribute('disabled');
        }
        var translateX = this.startIndex / this.totalItems * 100 * (this.totalItems / this.slidesPerBreakpoint[componentHandler.currentBreakpoint]) + "%";
        this.scrollIndicatorBar.style.transform = "translateX(" + translateX + ")";
        this.updatePagination();
    };
    // Update slides visibility on resize
    //
    // @public
    KomodoCarousel.prototype.updateSlidesVisibility = function() {
        this.startIndex = this.startIndex || 0;
        this.endIndex = this.startIndex + this.slidesPerBreakpoint[componentHandler.currentBreakpoint] - 1;
        if (this.endIndex > this.totalItems - 1 && !(this.totalItems - 1 < this.slidesPerBreakpoint[componentHandler.currentBreakpoint])) {
            this.startIndex = this.totalItems - 1 - this.slidesPerBreakpoint[componentHandler.currentBreakpoint];
            this.endIndex = this.totalItems - 1;
        }
        this.items[this.startIndex].scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: this.startIndex === 0 ? "center" : "start"
        });
        if (this.items.length > this.slidesPerBreakpoint[componentHandler.currentBreakpoint] && !this.paginationLabel && !this.scrollIndicatorBar) {
            this.createFooter();
            this.createScrollIndicator();
            this.scrollIndicatorBar.style.width = this.slidesPerBreakpoint[componentHandler.currentBreakpoint] / this.totalItems * 100 + "%";
            this.updatePagination();
        }
        if (this.items.length <= this.slidesPerBreakpoint[componentHandler.currentBreakpoint] && this.paginationLabel && this.scrollIndicatorBar) {
            this.removeFooterAndScrollbar();
        }
        if (this.paginationLabel) {
            this.updatePagination();
        }
    };
    KomodoCarousel.prototype['updateSlidesVisibility'] = KomodoCarousel.prototype.updateSlidesVisibility;
    // Bind the keyboard events with the accordion.
    //
    // @private
    KomodoCarousel.prototype.onKeyDown = function(event) {
        switch(event.keyCode){
            case window.componentHandler.keyCodes.SPACE:
                if (event.target === this.previousArrow || event.target === this.nextArrow) {
                    event.target.click();
                }
                break;
            case window.componentHandler.keyCodes.ARROW_UP:
                if (event.target.previousElementSibling) {
                    event.target.previousElementSibling.focus();
                }
                break;
            case window.componentHandler.keyCodes.ARROW_DOWN:
                if (event.target.nextElementSibling) {
                    event.target.nextElementSibling.focus();
                }
                break;
            case window.componentHandler.keyCodes.ARROW_LEFT:
                if (event.target === this.carouselPanelsContainer && this.previousArrow) {
                    this.previous(event);
                }
                break;
            case window.componentHandler.keyCodes.ARROW_RIGHT:
                if (event.target === this.carouselPanelsContainer && this.nextArrow) {
                    this.next(event);
                }
                break;
        }
    };
    // Update the pagination
    //
    // @public
    KomodoCarousel.prototype.updatePagination = function() {
        var range = this.startIndex === this.endIndex ? this.startIndex + 1 : this.startIndex + 1 + "-" + (this.endIndex + 1);
        this.paginationLabel.innerHTML = this.strings_.PAGINATION_LABEL.replace("$range", range);
        this.paginationLabel.innerHTML = this.paginationLabel.innerHTML.replace("$total", this.totalItems);
        this.scrollIndicatorBar.style.width = this.slidesPerBreakpoint[componentHandler.currentBreakpoint] / this.totalItems * 100 + "%";
    };
    KomodoCarousel.prototype['updatePagination'] = KomodoCarousel.prototype.updatePagination;
    // Update the carousel after adding dynamic content
    //
    // @public
    KomodoCarousel.prototype.upgradeCarousel = function() {
        this.initialize();
    };
    KomodoCarousel.prototype['upgradeCarousel'] = KomodoCarousel.prototype.upgradeCarousel;
    // The component registers itself. It can assume componentHandler is available
    // in the global scope.
    componentHandler.register({
        constructor: KomodoCarousel,
        classAsString: 'KomodoCarousel',
        cssClass: 'kmd-carousel',
        widget: true
    });
}
// Class constructor for Komodo ContextMenu component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo ContextMenu.
var KomodoContextMenu = function KomodoContextMenu(element) {
    this.element_ = element;
    this.trigger = document.querySelector(this.element_.getAttribute('data-kmd-context-menu-trigger-for'));
    this.initialize();
};
window['KomodoContextMenu'] = KomodoContextMenu;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoContextMenu.prototype.cssClasses_ = {
    ACTIVE: 'kmd-context-menu--active',
    TRIGGER: 'kmd-context-menu__trigger',
    NESTED_MENU: 'kmd-context-menu__nested',
    MENU_TOP: 'kmd-context-menu--top',
    MENU_LEFT: 'kmd-context-menu--left'
};
// Store strings for placeholders defined by this component that are used in Javascript.
//
// @enum {string}
// @private
KomodoContextMenu.prototype.strings_ = {};
// Komodo ContextMenu Initialize Method.
//
// @public
KomodoContextMenu.prototype.initialize = function() {
    this.onKeyDown = this.onKeyDown.bind(this);
    this.toggle = this.toggle.bind(this);
    this.element_.addEventListener('keydown', this.onKeyDown);
    this.trigger.addEventListener('click', this.toggle);
    this.trigger.addEventListener('keydown', this.onKeyDown);
};
KomodoContextMenu.prototype['initialize'] = KomodoContextMenu.prototype.initialize;
// Komodo ContextMenu Toggle Method.
//
// @public
KomodoContextMenu.prototype.toggle = function(event) {
    event.preventDefault();
    // Add a click listener to the document, to close the menu.
    var callback = (function(evt) {
        // Check to see if the document is processing the same event that
        // displayed the menu in the first place. If so, do nothing.
        // Also check to see if the menu is in the process of closing itself, and
        // do nothing in that case.
        // Also check if the clicked element is a menu item
        // if so, do nothing.
        if (evt !== event && evt.target.classList.contains(this.cssClasses_.TRIGGER) && evt.target.parentNode !== this.element_) {
            document.removeEventListener('click', callback);
            this.close = this.close.bind(this);
            this.close();
        }
    }).bind(this);
    if (!this.element_.classList.contains(this.cssClasses_.ACTIVE)) {
        this.open = this.open.bind(this);
        this.open();
        document.addEventListener('click', callback);
    }
};
KomodoContextMenu.prototype['toggle'] = KomodoContextMenu.prototype.toggle;
// Open Dropdown
//
// @public
KomodoContextMenu.prototype.open = function() {
    this.element_.dispatchEvent(componentHandler.createEvent('opening.komodo.contextMenu', null, false, true));
    this.trigger.classList.add(this.cssClasses_.ACTIVE);
    this.element_.classList.add(this.cssClasses_.ACTIVE);
    this.element_.focus();
    this.applyPosition();
    this.element_.dispatchEvent(componentHandler.createEvent('opened.komodo.contextMenu', null, false, true));
};
KomodoContextMenu.prototype['open'] = KomodoContextMenu.prototype.open;
// Close Dropdown
//
// @public
KomodoContextMenu.prototype.close = function() {
    this.element_.dispatchEvent(componentHandler.createEvent('closing.komodo.contextMenu', null, false, true));
    this.element_.classList.remove(this.cssClasses_.ACTIVE);
    this.trigger.classList.remove(this.cssClasses_.ACTIVE);
    this.element_.dispatchEvent(componentHandler.createEvent('closed.komodo.contextMenu', null, false, true));
};
KomodoContextMenu.prototype['close'] = KomodoContextMenu.prototype.close;
// Apply Position
//
// @private
KomodoContextMenu.prototype.applyPosition = function() {
    var triggerRect = this.trigger.getBoundingClientRect();
    var menuRect = this.element_.getBoundingClientRect();
    var offset = 10;
    // Set the primary menu position
    // Top is set to be immediately below the trigger.
    // Left is set to be immediately aligned to the left edge of the trigger.
    var top = triggerRect.top + triggerRect.height + window.scrollY;
    var left = triggerRect.left + window.scrollX;
    // If the menu is a left drop, right aligned menu, then adjust the left value
    if (this.element_.classList.contains(this.cssClasses_.MENU_LEFT)) {
        left = left + (triggerRect.width - menuRect.width);
    }
    // Nested Menu Positioning
    if (this.element_.classList.contains(this.cssClasses_.NESTED_MENU)) {
        top -= triggerRect.height + offset;
        // If the menu is a left drop, right aligned menu, then adjust the left value
        if (this.element_.classList.contains(this.cssClasses_.MENU_LEFT)) {
            left -= menuRect.width;
        } else {
            left += triggerRect.width;
        }
    }
    if (top + menuRect.height > window.innerHeight) {
        top = window.innerHeight - menuRect.height;
    }
    this.element_.style.top = top + 'px';
    this.element_.style.left = left + 'px';
};
// Komodo ContextMenu Key Interactions
//
// @public
KomodoContextMenu.prototype.onKeyDown = function(event) {
    switch(event.keyCode){
        case window.componentHandler.keyCodes.SPACE:
            if (event.target.classList.contains(this.cssClasses_.TRIGGER)) {
                this.toggle(event);
            } else {
                event.target.click();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_UP:
            if (event.target.previousElementSibling) {
                event.target.previousElementSibling.focus();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_DOWN:
            if (event.target.nextElementSibling) {
                event.target.nextElementSibling.focus();
            }
            break;
        case window.componentHandler.keyCodes.ESCAPE:
            this.close();
            break;
    }
};
KomodoContextMenu.prototype['onKeyDown'] = KomodoContextMenu.prototype.onKeyDown;
// Komodo ContextMenu deconstruction
//
// @public
KomodoContextMenu.prototype.destroy = function(event) {};
KomodoContextMenu.prototype['destroy'] = KomodoContextMenu.prototype.destroy;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoContextMenu,
    classAsString: 'KomodoContextMenu',
    cssClass: 'kmd-context-menu',
    widget: true
});
// Class constructor for Komodo Dropdown component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Dropdown.
var KomodoDropdown = function KomodoDropdown(element) {
    this.element_ = element;
    this.element_.classList.add('kmd-d-none');
    this.selected = new Set();
    this.values = [];
    this.emptyOpts = this.element_.querySelector('option[value=""]');
    this.createSelect();
    this.updateComponent = this.updateComponent.bind(this);
    this.element_.addEventListener('change', (function(e) {
        this.updateComponent().bind(this);
    }).bind(this));
};
window['KomodoDropdown'] = KomodoDropdown;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoDropdown.prototype.cssClasses_ = {
    ACTIVE: 'is-visible',
    FILTERED: 'kmd-dropdown__filtered',
    FILTERS_CONTAINER: 'kmd-dropdown__filters-container',
    LIST: 'kmd-list',
    LIST_ITEM: 'kmd-list__item',
    LIST_CONTAINER: 'kmd-dropdown__list-container',
    SEARCH: 'kmd-dropdown__search',
    SEARCH_CONTAINER: 'kmd-dropdown__search-container',
    SELECTED: 'kmd-dropdown__list-item-selected',
    TRIGGER: 'kmd-dropdown__trigger',
    CHECKBOX: 'kmd-checkbox',
    FOOTER: 'kmd-dropdown__footer',
    CHIP: 'kmd-chip',
    CHIP_CLOSE: 'kmd-chip__close'
};
// Store strings for placeholders defined by this component that are used in Javascript.
//
// @enum {string}
// @private
KomodoDropdown.prototype.strings_ = {
    CHECKBOX: '<input type="checkbox">',
    CHIP: '<div class="kmd-chip kmd-chip__intractable"><span class="kmd-chip__label">$value</span><span role="button" class="kmd-chip__close" tabindex="0">âœ•</span></div>',
    CLEAR_ALL: 'Clear all',
    FOOTER: '<div class="kmd-dropdown__footer kmd-d-none"></div>'
};
// Create select
// 
// @private
KomodoDropdown.prototype.createSelect = function() {
    this.createSelectTrigger();
    this.createSelectList();
    this.createListContainer();
    if (this.element_.getAttribute('data-komodo-filterable')) {
        this.createFiltersContainer();
    }
    this.element_.dispatchEvent(componentHandler.createEvent('available.komodo.dropdown', null, false, true));
};
// Create select trigger
// 
// @private
KomodoDropdown.prototype.createSelectTrigger = function() {
    this.selectTrigger = document.createElement('span');
    this.selectTrigger.id = "select-" + Date.now();
    this.selectTrigger.classList.add(this.cssClasses_.TRIGGER);
    this.selectTrigger.innerText = this.element_.options[0].innerText;
    this.element_.parentElement.insertBefore(this.selectTrigger, this.element_);
    this.selectTrigger.tabIndex = 0;
    this.toggle = this.toggle.bind(this);
    this.selectTrigger.addEventListener('click', this.toggle);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.selectTrigger.addEventListener('keydown', this.onKeyDown);
};
// Create select list
// 
// @private
KomodoDropdown.prototype.createSelectList = function() {
    this.selectList = document.createElement('ul');
    this.selectList.id = this.selectTrigger.id.replace("select", "select-list");
    this.selectList.classList.add(this.cssClasses_.LIST);
    this.selectList.addEventListener('keydown', this.onKeyDown);
    var options = [];
    var optionsObj = this.element_.options;
    var keys = Object.keys(optionsObj);
    for(var i = 0; i < keys.length; i++){
        options.push(optionsObj[keys[i]]);
    }
    for(var i = 0; i < options.length; i++){
        if (this.element_.multiple && options[i].value || !this.element_.multiple) {
            this.createSelectListItem(options[i]);
        }
    }
};
// Create select list item
// 
// @private
KomodoDropdown.prototype.createSelectListItem = function(option) {
    var item = document.createElement("li");
    item.classList.add(this.cssClasses_.LIST_ITEM);
    item.setAttribute('tabindex', 0);
    if (this.element_.multiple) {
        item.classList.add(this.cssClasses_.CHECKBOX);
        var checkbox = document.createElement("div");
        checkbox.innerHTML = this.strings_.CHECKBOX;
        checkbox = checkbox.querySelector("input");
        checkbox.id = this.element_.id + "-" + option.value;
        checkbox.value = option.value;
        item.appendChild(checkbox);
    }
    var text = document.createTextNode(option.innerText);
    item.appendChild(text);
    item.setAttribute("data-komodo-option-value", option.value);
    if (option.selected) {
        this.selectOption(option, item);
    }
    this.selectList.appendChild(item);
    this.toggleItem = this.toggleItem.bind(this);
    item.addEventListener('click', this.toggleItem);
    this.onKeyDown = this.onKeyDown.bind(this);
    item.addEventListener('keydown', function(e) {
        e.preventDefault();
        this.onKeyDown;
    });
};
// Create list container
// 
// @private
KomodoDropdown.prototype.createListContainer = function() {
    this.listContainer = document.createElement('div');
    this.listContainer.classList.add(this.cssClasses_.LIST_CONTAINER);
    this.listContainer.appendChild(this.selectList);
    this.element_.parentElement.insertBefore(this.listContainer, this.element_);
    this.onKeyDown = this.onKeyDown.bind(this);
};
// Create filters container
// 
// @private
KomodoDropdown.prototype.createFiltersContainer = function() {
    this.filtersContainer = document.createElement('div');
    this.filtersContainer.classList.add(this.cssClasses_.FILTERS_CONTAINER);
    this.element_.parentElement.insertBefore(this.filtersContainer, this.element_);
    this.dropdownFooter = document.createElement('div');
    this.dropdownFooter.innerHTML = this.strings_.FOOTER;
    this.dropdownFooter = this.dropdownFooter.firstChild;
    this.listContainer.appendChild(this.dropdownFooter);
};
// Toggle List.
//
// @public
KomodoDropdown.prototype.toggle = function(event) {
    event.preventDefault();
    // Add a click listener to the document, to close the menu.
    var callback = (function(evt) {
        // Check to see if the document is processing the same event that
        // displayed the menu in the first place. If so, do nothing.
        // Also check to see if the menu is in the process of closing itself, and
        // do nothing in that case.
        // Also check if the clicked element is a menu item
        // if so, do nothing.
        if (evt !== event && evt.target.parentNode !== this.element_ && evt.target !== this.searchField) {
            if (this.element_.multiple && evt.target.nodeName !== "LI" && evt.target.parentNode && evt.target.parentNode.nodeName !== "LI" || !this.element_.multiple) {
                document.removeEventListener('click', callback);
                this.close = this.close.bind(this);
                this.close();
            }
        }
    }).bind(this);
    if (!this.listContainer.classList.contains(this.cssClasses_.ACTIVE)) {
        this.open = this.open.bind(this);
        this.open();
        document.addEventListener('click', callback);
    }
};
KomodoDropdown.prototype['toggle'] = KomodoDropdown.prototype.toggle;
// Open Dropdown
// 
// @public
KomodoDropdown.prototype.open = function() {
    this.element_.dispatchEvent(componentHandler.createEvent('opening.komodo.dropdown', null, false, true));
    this.selectTrigger.classList.add(this.cssClasses_.ACTIVE);
    this.listContainer.classList.add(this.cssClasses_.ACTIVE);
    this.applyPosition();
    this.element_.dispatchEvent(componentHandler.createEvent('opened.komodo.dropdown', null, false, true));
    this.selectList.focus();
};
KomodoDropdown.prototype['open'] = KomodoDropdown.prototype.open;
// Close Dropdown
// 
// @public
KomodoDropdown.prototype.close = function() {
    this.element_.dispatchEvent(componentHandler.createEvent('closing.komodo.dropdown', null, false, true));
    if (this.searchField) this.searchField.value = null;
    for(var i = 0; i < this.selectList.children.length; i++){
        var item = this.selectList.children[i];
        item.classList.remove('kmd-d-none');
    }
    this.listContainer.classList.remove(this.cssClasses_.ACTIVE);
    this.selectTrigger.classList.remove(this.cssClasses_.ACTIVE);
    this.element_.dispatchEvent(componentHandler.createEvent('closed.komodo.dropdown', null, false, true));
};
KomodoDropdown.prototype['close'] = KomodoDropdown.prototype.close;
// Select Item
// 
// @private
KomodoDropdown.prototype.toggleItem = function(event) {
    var option = this.element_.querySelector("option[value='" + event.target.getAttribute("data-komodo-option-value") + "']");
    var item = event.target;
    while(item && item.nodeName !== "LI"){
        item = item.parentNode;
    }
    if (this.selected.has(item) && this.element_.multiple) {
        this.deselectOption(option, item);
    } else {
        this.selectOption(option, item);
    }
    var changeEvent = document.createEvent('CustomEvent');
    changeEvent.initCustomEvent('change.komodo.dropdown', false, true, {
        selectedOptions: this.element_.selectedOptions
    });
    this.element_.dispatchEvent(changeEvent);
    this.toggle(event);
};
// Select Option
// 
// @public
KomodoDropdown.prototype.selectOption = function(option, item) {
    if (this.element_.multiple) {
        this.selected.add(item);
        item.classList.add(this.cssClasses_.SELECTED);
        var input = item.querySelector("input");
        input.checked = "checked";
        option.setAttribute("selected", "selected");
        this.emptyOpts.removeAttribute("selected");
        this.selectTrigger.innerText = Object.values(this.element_.selectedOptions).map(function(opt) {
            return opt.innerText;
        }).join(", ");
        if (this.filtersContainer) {
            this.selectTrigger.classList.add(this.cssClasses_.FILTERED);
            this.filtersContainer.innerHTML = "";
            Object.values(this.element_.selectedOptions).forEach((function(opt) {
                this.addChip(opt.innerText, opt.value);
            }).bind(this));
        }
        if (this.dropdownFooter) {
            this.displayCounter();
        }
    } else {
        if (this.selected.size > 0) {
            var currentlySelected;
            if (this.selected.size > 0) {
                var selectedItems = this.selected;
                var firstSelected;
                selectedItems.forEach(function(item) {
                    if (firstSelected === undefined) {
                        firstSelected = item;
                    }
                });
                currentlySelected = firstSelected;
            }
            currentlySelected.classList.remove(this.cssClasses_.SELECTED);
            this.selected.delete(currentlySelected);
            this.selectTrigger.innerHTML = this.placeholder;
        }
        option.setAttribute("selected", "selected");
        item.classList.add(this.cssClasses_.SELECTED);
        this.selectTrigger.innerHTML = option.innerHTML;
        this.selected.add(item);
    }
};
KomodoDropdown.prototype['selectOption'] = KomodoDropdown.prototype.selectOption;
// Deselect Option
// 
// @public
KomodoDropdown.prototype.deselectOption = function(option, item) {
    option.removeAttribute("selected");
    item.classList.remove(this.cssClasses_.SELECTED);
    if (this.element_.multiple) {
        var input = item.querySelector("input");
        input.checked = false;
    }
    this.selected.delete(item);
    if (this.selected.size === 0) {
        this.emptyOpts.setAttribute("selected", "selected");
        this.selectTrigger.innerText = this.emptyOpts.innerText;
        this.selectTrigger.classList.remove(this.cssClasses_.FILTERED);
        if (this.filtersContainer) {
            this.filtersContainer.innerHTML = "";
        }
    } else {
        this.selectTrigger.innerText = Object.values(this.element_.selectedOptions).map(function(opt) {
            return opt.innerText;
        }).join(", ");
        if (this.filtersContainer) {
            this.filtersContainer.innerHTML = "";
            Object.values(this.element_.selectedOptions).forEach((function(opt) {
                this.addChip(opt.innerText, opt.value);
            }).bind(this));
        }
    }
    if (this.dropdownFooter) {
        this.displayCounter();
    }
};
KomodoDropdown.prototype['deselectOption'] = KomodoDropdown.prototype.deselectOption;
// Deselect all options
// 
// @public
KomodoDropdown.prototype.deselectOptions = function(event) {
    Object.values(this.element_.selectedOptions).forEach(function(opt) {
        opt.removeAttribute('selected');
    });
    this.selected.forEach((function(el) {
        el.querySelector('input').checked = false;
        el.classList.remove(this.cssClasses_.SELECTED);
    }).bind(this));
    this.emptyOpts.setAttribute('selected', 'selected');
    this.selectTrigger.classList.remove(this.cssClasses_.FILTERED);
    this.selectTrigger.innerText = this.emptyOpts.innerText;
    this.filtersContainer.innerHTML = "";
    this.dropdownFooter.innerHTML = "";
    this.dropdownFooter.classList.add("kmd-d-none");
    this.selected = new Set();
    this.toggle(event);
    this.element_.dispatchEvent(componentHandler.createEvent('change', {
        komodo: true
    }, false, true));
};
KomodoDropdown.prototype['deselectOptions'] = KomodoDropdown.prototype.deselectOptions;
// Display options counter
// 
// @private
KomodoDropdown.prototype.displayCounter = function() {
    if (!this.emptyOpts.selected) {
        this.displayedCounter = document.createElement('div');
        this.displayedCounter.innerHTML = this.strings_.CHIP.replace('$value', this.element_.selectedOptions.length);
        this.displayedCounter = this.displayedCounter.firstChild;
        this.displayedCounter.id = this.element_.id + "-displayed-counter";
        this.selectTrigger.appendChild(this.displayedCounter);
        var closeButton = this.displayedCounter.querySelector('.kmd-chip__close');
        this.deselectOptions = this.deselectOptions.bind(this);
        closeButton.addEventListener('click', this.deselectOptions);
        this.footerCounter();
    } else {
        this.displayedCounter.remove();
        this.dropdownFooter.classList.add("kmd-d-none");
    }
};
// Add/Update footer counter
// 
// @private
KomodoDropdown.prototype.footerCounter = function() {
    this.dropdownFooter.innerHTML = "";
    var selected = document.createElement('span');
    selected.innerText = this.element_.selectedOptions.length + ' selected';
    this.dropdownFooter.appendChild(selected);
    this.clearAll = document.createElement('span');
    this.clearAll.innerText = this.strings_.CLEAR_ALL;
    this.clearAll.classList.add("kmd-dropdown__footer-clearall");
    this.clearAll.setAttribute('tabindex', 0);
    this.dropdownFooter.appendChild(this.clearAll);
    this.clearAll.addEventListener("click", this.deselectOptions.bind(this));
    this.clearAll.addEventListener("keydown", this.onKeyDown.bind(this));
    this.dropdownFooter.classList.remove("kmd-d-none");
};
// Add filter chip
// 
// @private
KomodoDropdown.prototype.addChip = function(text, value) {
    var chip = document.createElement('div');
    chip.innerHTML = this.strings_.CHIP.replace('$value', text);
    chip = chip.firstChild;
    chip.setAttribute('data-komodo-filterable-value', value);
    chip.setAttribute('tabindex', 0);
    var chipClose = chip.getElementsByClassName(this.cssClasses_.CHIP_CLOSE)[0];
    chipClose.addEventListener("keydown", this.onKeyDown.bind(this));
    chipClose.addEventListener("click", (function(e) {
        var chip = e.target.closest(".kmd-chip");
        var option = this.element_.querySelector("option[value=\"" + chip.getAttribute("data-komodo-filterable-value") + "\"]");
        var item = this.listContainer.querySelector("[data-komodo-option-value=\"" + chip.getAttribute("data-komodo-filterable-value") + "\"]");
        this.deselectOption(option, item);
    }).bind(this));
    this.filtersContainer.appendChild(chip);
};
// Update component
// This function is to update the component based on programmatic updates to the HTML element.
// 
// @public
KomodoDropdown.prototype.updateComponent = function() {
    var options = this.element_.options;
    for(var i = 0; i < options.length; i++){
        var opt = options[i];
        if (opt.value) {
            var item = this.listContainer.querySelector("[data-komodo-option-value='" + opt.value + "']");
            if (opt.selected) {
                this.selectOption(opt, item);
            } else {
                this.deselectOption(opt, item);
            }
        }
    }
};
KomodoDropdown.prototype['updateComponent'] = KomodoDropdown.prototype.updateComponent;
// Apply Position
// 
// @private
KomodoDropdown.prototype.applyPosition = function() {
    this.selectList.style.width = this.selectTrigger.getBoundingClientRect().width + 'px';
};
// Bind the keyboard events with the dropdown.
// 
// @public
KomodoDropdown.prototype.onKeyDown = function(event) {
    switch(event.keyCode){
        case window.componentHandler.keyCodes.ENTER:
            if (event.target === this.selectTrigger) {
                this.toggle(event);
            } else {
                event.target.click();
            }
            break;
        case window.componentHandler.keyCodes.SPACE:
            event.preventDefault();
            if (event.target.classList.contains(this.cssClasses_.LIST_ITEM)) {
                this.toggleItem(event);
                if (!this.element_.multiple) {
                    this.close();
                    this.selectTrigger.focus();
                }
            } else {
                event.target.click();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_UP:
            if (event.target === this.selectList) {
                event.preventDefault();
                this.selectList.lastChild.focus();
            } else if (event.target.previousElementSibling) {
                event.target.previousElementSibling.focus();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_DOWN:
            if (event.target === this.selectList) {
                event.preventDefault();
                this.selectList.firstChild.focus();
            } else if (event.target.nextElementSibling) {
                event.target.nextElementSibling.focus();
            } else if (this.dropdownFooter) {
                this.clearAll.focus();
            }
            break;
        case window.componentHandler.keyCodes.ESCAPE:
            this.close();
            this.selectTrigger.focus();
            break;
    }
};
KomodoDropdown.prototype['onKeyDown'] = KomodoDropdown.prototype.onKeyDown;
// Deconstruct the dropdown component.
// 
// @public
KomodoDropdown.prototype.destroy = function() {
    this.selectTrigger.remove();
    this.listContainer.remove();
    this.filtersContainer.remove();
    this.updateComponent = this.updateComponent.bind(this);
    this.element_.removeEventListener('change', this.updateComponent);
    this.element_.classList.remove("kmd-d-none");
};
KomodoDropdown.prototype['destroy'] = KomodoDropdown.prototype.destroy;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoDropdown,
    classAsString: 'KomodoDropdown',
    cssClass: 'kmd-dropdown',
    widget: true
});
// Class constructor for Komodo Password component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Password.
var KomodoPassword = function KomodoPassword(element) {
    this.element_ = element;
    this.input = this.element_.querySelector('input');
    this.visibility = this.element_.querySelector("." + this.cssClasses_.VISIBILITY);
    this.toggle = this.toggle.bind(this);
    this.visibility.addEventListener('click', this.toggle);
};
window['KomodoPassword'] = KomodoPassword;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoPassword.prototype.cssClasses_ = {
    VISIBILITY: 'kmd-field__visibility',
    VISIBLE: 'kmd-field__visible'
};
// Toggle the password field.
// 
// @public
KomodoPassword.prototype.toggle = function(event) {
    if (this.visibility.classList.contains(this.cssClasses_.VISIBLE)) {
        this.input.type = 'password';
        this.visibility.classList.remove(this.cssClasses_.VISIBLE);
        this.element_.dispatchEvent(componentHandler.createEvent('hiddenPassword.komodo.passwordField', {}, false, true));
    } else {
        this.input.type = 'text';
        this.visibility.classList.add(this.cssClasses_.VISIBLE);
        this.element_.dispatchEvent(componentHandler.createEvent('visiblePassword.komodo.passwordField', {}, false, true));
    }
};
KomodoPassword.prototype['toggle'] = KomodoPassword.prototype.toggle;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoPassword,
    classAsString: 'KomodoPassword',
    cssClass: 'kmd-field--password',
    widget: true
});
// Class constructor for Komodo Quantity Selector component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Password.
var KomodoQuantitySelector = function KomodoQuantitySelector(element) {
    this.element_ = element;
    this.input = this.element_.querySelector('input');
    this.initialize();
    this.decrease.addEventListener('click', this.decreaseQuantity.bind(this));
    this.increase.addEventListener('click', this.increaseQuantity.bind(this));
    this.element_.addEventListener('keydown', this.onKeyDown.bind(this));
    this.input.addEventListener('change', this.manageState.bind(this));
};
window['KomodoQuantitySelector'] = KomodoQuantitySelector;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoQuantitySelector.prototype.cssClasses_ = {
    DELETE: 'kmd-field--quantity-selector__decrease--delete',
    ECOMMERCE: 'kmd-field--quantity-selector-ecommerce'
};
// Store strings for placeholders defined by this component that are used in Javascript.
//
// @enum {string}
// @private
KomodoQuantitySelector.prototype.strings_ = {
    DECREASE: '<span class="kmd-field--quantity-selector__decrease"></span>',
    INCREASE: '<span class="kmd-field--quantity-selector__increase"></span>'
};
// Build the quantity selector
//
// @public
KomodoQuantitySelector.prototype.initialize = function(event) {
    this.element_.setAttribute('tabindex', 0);
    var cont = document.createElement("div");
    cont.innerHTML = this.strings_.DECREASE + this.strings_.INCREASE;
    this.decrease = cont.firstChild;
    this.element_.insertBefore(this.decrease, this.input);
    this.increase = cont.lastChild;
    this.element_.appendChild(this.increase);
    this.decrease.setAttribute('role', 'button');
    this.increase.setAttribute('role', 'button');
    if (this.element_.classList.contains(this.cssClasses_.ECOMMERCE)) {
        this.input.min = 1;
        this.input.value = 1;
    }
    if (this.input.min && this.input.value !== null) {
        this.input.value = parseInt(this.input.min);
    }
    this.manageState();
};
KomodoQuantitySelector.prototype['initialize'] = KomodoQuantitySelector.prototype.initialize;
// Increase the quantity
//
// @public
KomodoQuantitySelector.prototype.increaseQuantity = function(event) {
    if (!this.increase.disabled) {
        var currentValue = parseInt(this.input.value, 10);
        var step = parseInt(this.input.getAttribute('step')) || 1;
        var max = parseInt(this.input.getAttribute('max'));
        var newValue = currentValue + step;
        if (!isNaN(max) && newValue > max) {
            newValue = max;
        }
        this.input.value = newValue;
        this.manageState();
    }
};
KomodoQuantitySelector.prototype['increaseQuantity'] = KomodoQuantitySelector.prototype.increaseQuantity;
// Decrease the quantity
//
// @public
KomodoQuantitySelector.prototype.decreaseQuantity = function(event) {
    if (this.decrease.classList.contains(this.cssClasses_.DELETE)) {
        this.element_.dispatchEvent(componentHandler.createEvent('delete.komodo.quantitySelector', null, false, true));
    } else {
        if (!this.decrease.disabled) {
            var currentValue = parseInt(this.input.value, 10);
            var step = parseInt(this.input.getAttribute('step')) || 1;
            var min = parseInt(this.input.getAttribute('min'));
            var newValue = currentValue - step;
            if (!isNaN(min) && newValue < min) {
                newValue = min;
            }
            this.input.value = newValue;
            this.manageState();
        }
    }
};
KomodoQuantitySelector.prototype['decreaseQuantity'] = KomodoQuantitySelector.prototype.decreaseQuantity;
// Manage the quantity selector
//
// @private
KomodoQuantitySelector.prototype.manageState = function(event) {
    if (parseInt(this.input.value) === 1 && this.element_.classList.contains(this.cssClasses_.ECOMMERCE)) {
        this.decrease.classList.add(this.cssClasses_.DELETE);
    } else {
        this.decrease.classList.remove(this.cssClasses_.DELETE);
    }
    if (parseInt(this.input.value) === parseInt(this.input.min) && !this.element_.classList.contains(this.cssClasses_.ECOMMERCE)) {
        this.decrease.setAttribute('disabled', 'disabled');
    } else {
        this.decrease.removeAttribute('disabled');
    }
    if (parseInt(this.input.value) === parseInt(this.input.max)) {
        this.increase.setAttribute('disabled', 'disabled');
    } else {
        this.increase.removeAttribute('disabled');
    }
};
// Bind the keyboard events with the accordion.
//
// @public
KomodoQuantitySelector.prototype.onKeyDown = function(event) {
    switch(event.keyCode){
        case window.componentHandler.keyCodes.ENTER:
            event.preventDefault();
            if (event.target === this.increase) {
                this.increaseQuantity();
            } else if (event.target === this.decrease) {
                this.decreaseQuantity();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_UP:
            event.preventDefault();
            this.increaseQuantity();
            break;
        case window.componentHandler.keyCodes.ARROW_DOWN:
            event.preventDefault();
            this.decreaseQuantity();
            break;
    }
};
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoQuantitySelector,
    classAsString: 'KomodoQuantitySelector',
    cssClass: 'kmd-field--quantity-selector',
    widget: true
});
// Class constructor for Komodo Notification component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Notification.
export var KomodoNotification = function KomodoNotification(element) {
    this.element_ = element;
    this.container = this.element_.parentElement;
    this.close = this.element_.getElementsByClassName(this.cssClasses_.CLOSE)[0];
    this.autoClose = this.element_.dataset.komodoNotificationAutoClose || false;
    this.message = this.element_.getElementsByClassName(this.cssClasses_.MESSAGE)[0];
    this.hide = this.hide.bind(this);
    if (this.validate()) {
        this.initialize();
    }
};
window['KomodoNotification'] = KomodoNotification;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoNotification.prototype.cssClasses_ = {
    MESSAGE: 'kmd-notification__message',
    CLOSE: 'kmd-notification__close',
    VISIBLE: 'kmd-notification--visible',
    REMOVE: 'kmd-notification--removing',
    CONTEXT: {
        ERROR: 'kmd-notification--error',
        WARNING: 'kmd-notification--warning',
        INFO: 'kmd-notification--info',
        SUCCESS: 'kmd-notification--success'
    }
};
// Initialize the notification.
// 
// @public
KomodoNotification.prototype.initialize = function(event) {
    this.element_.dispatchEvent(componentHandler.createEvent('presenting.komodo.notification', {}, false, true));
    this.element_.classList.add(this.cssClasses_.VISIBLE);
    this.element_.dispatchEvent(componentHandler.createEvent('presented.komodo.notification', {}, false, true));
    if (this.autoClose) {
        setTimeout(this.hide, parseInt(this.autoClose));
    } else if (this.close) {
        this.close.addEventListener('click', this.hide);
    }
};
KomodoNotification.prototype['initialize'] = KomodoNotification.prototype.initialize;
// Hide the notification.
// 
// @public
KomodoNotification.prototype.hide = function(event) {
    this.element_.dispatchEvent(componentHandler.createEvent('removing.komodo.notification', {}, false, true));
    this.element_.classList.add(this.cssClasses_.REMOVE);
    if (this.element_) {
        this.element_.dispatchEvent(componentHandler.createEvent('removed.komodo.notification', {}, false, true));
        var removeElement = function removeElement() {
            if (this.container.getElementsByClassName(this.cssClasses_.MESSAGE).length === 1) {
                this.container.remove();
            }
            this.element_.remove();
        };
        setTimeout(removeElement.bind(this), 400);
        this.element_.classList.add('kmd-d-none');
    }
};
KomodoNotification.prototype['hide'] = KomodoNotification.prototype.hide;
// Validate the markup follows the set out guidelines.
//
// @public
KomodoNotification.prototype.validate = function() {
    var _this_message_innerText, _this_message, _this_message_innerHTML, _this_message1;
    var errors = {
        element: this.element_
    };
    var conClassAvailable = false;
    var contextValues = Object.keys(this.cssClasses_.CONTEXT).map(function(key) {
        return this.cssClasses_.CONTEXT[key];
    }, this);
    for(var i = 0; i < contextValues.length; i++){
        conClassAvailable = conClassAvailable || this.element_.className.indexOf(contextValues[i]) !== -1;
        if (conClassAvailable) break;
    }
    if (!conClassAvailable) {
        errors['Context Class Unavailable:'] = 'No context class available, defaulting to informational.';
        this.element_.classList.add(this.cssClasses_.CONTEXT.INFO);
    }
    // Feedback message komodo team
    if (!this.message || this.message && ((_this_message = this.message) === null || _this_message === void 0 ? void 0 : (_this_message_innerText = _this_message.innerText) === null || _this_message_innerText === void 0 ? void 0 : _this_message_innerText.length) === 0 && ((_this_message1 = this.message) === null || _this_message1 === void 0 ? void 0 : (_this_message_innerHTML = _this_message1.innerHTML) === null || _this_message_innerHTML === void 0 ? void 0 : _this_message_innerHTML.length) === 0) {
        errors['Message Missing:'] = 'No notification message available, please add some details.';
    }
    if (!this.close && !this.autoClose) {
        errors['Close Configuration Missing:'] = 'No close icon available for the notification message. This notification will be close automatically in 1s.';
        setTimeout(this.hide, 1000);
    }
    if (Object.keys(errors).length > 1) {
        console.group('Komodo Notification Error');
        for(var error in errors){
            console.error(error, errors[error]);
        }
        console.groupEnd();
    }
    return true;
};
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoNotification,
    classAsString: 'KomodoNotification',
    cssClass: 'kmd-notification',
    widget: true
});
// Class constructor for Komodo Remote Request component.
// Implements Komodo component design pattern.
//
// @constructor
// @param {HTML Element} The element that will be upgraded to be Komodo Remote Request component.
var KomodoRemoteRequest = function KomodoRemoteRequest(element) {
    this.element_ = element;
    this.responseConatiners = document.querySelectorAll(this.element_.getAttribute("data-remote-containers"));
    this.validateRequest();
    this.requestMethod = this.element_.getAttribute("data-remote-method");
};
window['KomodoRemoteRequest'] = KomodoRemoteRequest;
KomodoRemoteRequest.prototype.validateRequest = function() {
    var errors = {
        error: "KomodoRemoteRequest: Failed Initialization",
        messages: [],
        element: this.element_
    };
    if (!this.element_.getAttribute("data-remote-method")) {
        this.element_.setAttribute("data-remote-method", "GET");
        console.warn({
            element: this.element_,
            message: '"data-remote-method" is a required attribute. It has been defaulted to GET.'
        });
    } else if ([
        "GET",
        "POST"
    ].indexOf(this.element_.getAttribute("data-remote-method")) < 0) {
        errors.messages.push('"data-remote-method" attribute does not have a valid value. It much be either GET or POST only.');
    }
    if (this.responseConatiners.length === 0) {
        errors.messages.push('"data-remote-containers" CSS selectors do not match any elements on the page. Please ensure to have a container ready.');
    }
    if (!this.element_.getAttribute("data-remote-url")) {
        errors.messages.push('"data-remote-url" is a required attribute. Please ensure to set the URL to point to a functioning end-point.');
    }
    if (errors.messages.length > 0) {
        console.error(errors);
        return false;
    }
    return true;
};
KomodoRemoteRequest.prototype['validateRequest'] = KomodoRemoteRequest.prototype.validateRequest;
KomodoRemoteRequest.prototype.destroy = function() {};
KomodoRemoteRequest.prototype['destroy'] = KomodoRemoteRequest.prototype.destroy;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoRemoteRequest,
    classAsString: 'KomodoRemoteRequest',
    cssClass: 'kmd-remote-request',
    widget: true
});
// Class constructor for Komodo Tab Item component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Accordion Item.
var KomodoTabItem = function KomodoTabItem(element) {
    this.element_ = element;
    this.element_.setAttribute('tabindex', 0);
    this.panel = this.element_.parentElement.parentElement.querySelector("." + this.cssClasses_.PANELS + " " + this.element_.dataset.komodoTabPanel);
    this.panel.setAttribute('tabindex', 0);
    this.activate = this.activate.bind(this);
    this.element_.addEventListener('click', this.activate);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.element_.addEventListener('keydown', this.onKeyDown);
};
window['KomodoTabItem'] = KomodoTabItem;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoTabItem.prototype.cssClasses_ = {
    ACTIVE: 'active',
    TABS: 'kmd-tabs',
    PANELS: 'kmd-tabs__panels'
};
// Activate the tab item.
//
// @public
KomodoTabItem.prototype.activate = function() {
    this.element_.dispatchEvent(componentHandler.createEvent('activating.komodo.tabItem', {
        target: this.element_,
        panel: this.panel
    }, false, true));
    this.element_.classList.add(this.cssClasses_.ACTIVE);
    this.element_.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
    });
    this.panel.classList.add(this.cssClasses_.ACTIVE);
    this.element_.dispatchEvent(componentHandler.createEvent('activated.komodo.tabItem', {
        target: this.element_,
        panel: this.panel
    }, false, true));
};
KomodoTabItem.prototype['activate'] = KomodoTabItem.prototype.activate;
// Deactivate the tab item.
//
// @public
KomodoTabItem.prototype.deactivate = function() {
    this.element_.dispatchEvent(componentHandler.createEvent('deactivating.komodo.tabItem', {}, false, true));
    this.element_.classList.remove(this.cssClasses_.ACTIVE);
    this.panel.classList.remove(this.cssClasses_.ACTIVE);
    this.element_.dispatchEvent(componentHandler.createEvent('deactivated.komodo.tabItem', {}, false, true));
};
KomodoTabItem.prototype['deactivate'] = KomodoTabItem.prototype.deactivate;
// Bind the keyboard events with the tab item.
//
// @private
KomodoTabItem.prototype.onKeyDown = function(event) {
    switch(event.keyCode){
        case window.componentHandler.keyCodes.SPACE:
            this.activate();
            this.panel.focus();
            break;
        case window.componentHandler.keyCodes.ARROW_UP:
            if (event.target.previousElementSibling) {
                event.target.previousElementSibling.focus();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_DOWN:
            if (event.target.nextElementSibling) {
                event.target.nextElementSibling.focus();
            }
            break;
    }
};
// Deconstruct the tab item component.
//
// @public
KomodoTabItem.prototype.destroy = function() {
    this.element_.removeEventListener('click', this.activate);
    this.element_.removeEventListener('keydown', this.onKeyDown);
};
KomodoTabItem.prototype['destroy'] = KomodoTabItem.prototype.destroy;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoTabItem,
    classAsString: 'KomodoTabItem',
    cssClass: 'kmd-tabs__list-item',
    widget: true
});
// Class constructor for Komodo Tab component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Tab.
var KomodoTab = function KomodoTab(element) {
    this.element_ = element;
    this.tabs = this.element_.querySelectorAll("." + this.cssClasses_.TABS_LIST + " ." + this.cssClasses_.TABS_LIST_ITEM);
    this.komodoTabsItems = {};
    for(var i = 0; i < Object.keys(this.tabs).length; i++){
        var tab = this.tabs[Object.keys(this.tabs)[i]];
        this.komodoTabsItems[tab.dataset.komodoTabPanel] = componentHandler.registeredInstances.KomodoTabItem[tab.dataset.komodoTabitemComponentId];
        tab.addEventListener('activating.komodo.tabItem', (function(e) {
            if (this.activeTab && this.activeTab.element_ !== e.target) {
                this.activeTab.deactivate();
            }
        }).bind(this));
        tab.addEventListener('activated.komodo.tabItem', (function(e) {
            this.activeTab = this.komodoTabsItems[e.target.dataset.komodoTabPanel];
        }).bind(this));
    }
    this.activateFirstTab();
    this.onKeyDown = this.onKeyDown.bind(this);
    this.element_.addEventListener('keydown', this.onKeyDown);
};
window['KomodoTab'] = KomodoTab;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoTab.prototype.cssClasses_ = {
    ACTIVE: 'active',
    TABS_LIST: 'kmd-tabs__list',
    TABS_LIST_ITEM: 'kmd-tabs__list-item'
};
// Activate first tab
//
// @private
KomodoTab.prototype.activateFirstTab = function() {
    componentHandler.registeredInstances.KomodoTabItem[this.tabs[0].dataset.komodoTabitemComponentId].activate();
};
// Bind the keyboard events with the tabs.
// 
// @private
KomodoTab.prototype.onKeyDown = function(event) {
    switch(event.keyCode){
        case window.componentHandler.keyCodes.ARROW_UP:
            if (event.target.previousElementSibling) {
                event.target.previousElementSibling.focus();
            }
            break;
        case window.componentHandler.keyCodes.ARROW_DOWN:
            if (event.target.nextElementSibling) {
                event.target.nextElementSibling.focus();
            }
            break;
    }
};
// Deconstruct the komodo tab component.
// 
// @public
KomodoTab.prototype.destroy = function() {
    for(var i = 0; i < Object.keys(this.tabs).length; i++){
        this.tabs[Object.keys(this.tabs)[i]].destroy();
    }
};
KomodoTab.prototype['destroy'] = KomodoTab.prototype.destroy;
// The component registers itself. It can assume componentHandler is available
// in the global scope.
componentHandler.register({
    constructor: KomodoTab,
    classAsString: 'KomodoTab',
    cssClass: 'kmd-tabs',
    widget: true
});
// Class constructor for Komodo Tooltip component.
// Implements Komodo component design pattern
//
// @constructor
// @param {HTMLElement} The element that will be upgraded to be Komodo Accordion.
export var KomodoTooltip = function KomodoTooltip(element) {
    this.element_ = element;
    this.elementTrigger_ = document.querySelector(element.dataset.kmdTooltipFor);
    this.toggle = this.toggle.bind(this);
    if (this.validate()) {
        // Toggle Tooltip
        this.elementTrigger_.addEventListener('focus', this.toggle);
        // Toggle Tooltip
        this.elementTrigger_.addEventListener('mouseover', this.toggle);
    }
};
window['KomodoTooltip'] = KomodoTooltip;
// Store strings for class names defined by this component that are used in
// Javascript. This allows us to simply change it in one place should we decide
// to modify at a later date.
//
// @enum {string}
// @private
KomodoTooltip.prototype.cssClasses_ = {
    ACTIVE: 'is-visible',
    RIGHT: 'kmd-tooltip--right',
    RIGHT_TOP: 'kmd-tooltip--right-top',
    RIGHT_BOTTOM: 'kmd-tooltip--right-bottom',
    LEFT: 'kmd-tooltip--left',
    LEFT_TOP: 'kmd-tooltip--left-top',
    LEFT_BOTTOM: 'kmd-tooltip--left-bottom',
    BOTTOM: 'kmd-tooltip--bottom',
    BOTTOM_LEFT: 'kmd-tooltip--bottom-left',
    BOTTOM_RIGHT: 'kmd-tooltip--bottom-right',
    TOP: 'kmd-tooltip--top',
    TOP_LEFT: 'kmd-tooltip--top-left',
    TOP_RIGHT: 'kmd-tooltip--top-right'
};
// Toggle Menu.
//
// @public
KomodoTooltip.prototype.toggle = function(event) {
    event.preventDefault();
    // Add a click listener to the document, to close the menu.
    var callback = (function(evt) {
        // Check to see if the document is processing the same event that
        // displayed the menu in the first place. If so, do nothing.
        // Also check to see if the menu is in the process of closing itself, and
        // do nothing in that case.
        // Also check if the clicked element is a menu item
        // if so, do nothing.
        if (evt !== event && evt.target.parentNode !== this.element_) {
            document.removeEventListener('click', callback);
            this.element_.dispatchEvent(componentHandler.createEvent('closing.komodo.tooltip', null, false, true));
            this.element_.classList.remove(this.cssClasses_.ACTIVE);
            this.element_.dispatchEvent(componentHandler.createEvent('closed.komodo.tooltip', null, false, true));
        }
    }).bind(this);
    if (!this.element_.classList.contains(this.cssClasses_.ACTIVE)) {
        this.element_.dispatchEvent(componentHandler.createEvent('opening.komodo.tooltip', null, false, true));
        this.applyPosition_();
        this.element_.classList.add(this.cssClasses_.ACTIVE);
        this.element_.dispatchEvent(componentHandler.createEvent('opened.komodo.tooltip', null, false, true));
        document.addEventListener('click', callback);
        this.elementTrigger_.addEventListener('blur', callback);
    }
};
KomodoTooltip.prototype['toggle'] = KomodoTooltip.prototype.toggle;
KomodoTooltip.prototype.validate = function() {
    if (!this.elementTrigger_) {
        console.error({
            element: this.element_,
            errors: [
                {
                    error: "Komodo Tooltip: Tooltip for element is not present.",
                    message: "Tooltip for component is required for the activation of the tooltip."
                }
            ]
        });
        return false;
    } else {
        return true;
    }
};
//
// Calculates the initial clip (for opening the menu) and applies it.
// This allows us to animate from or to the correct point,
// that is, the point it's aligned to in the "for" element.
//
// @param {number} height Height of the clip rectangle
// @param {number} width Width of the clip rectangle
// @private
KomodoTooltip.prototype.applyPosition_ = function() {
    var positioning = this.elementTrigger_.getBoundingClientRect();
    var tooltipPosition = this.element_.getBoundingClientRect();
    if (this.element_.classList.contains(this.cssClasses_.BOTTOM)) {
        this.element_.style.top = positioning.y + positioning.height + 5 + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width / 2 + "px";
    } else if (this.element_.classList.contains(this.cssClasses_.BOTTOM_LEFT)) {
        this.element_.style.top = positioning.y + positioning.height + 5 + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width * 0.08 + "px";
    } else if (this.element_.classList.contains(this.cssClasses_.BOTTOM_RIGHT)) {
        this.element_.style.top = positioning.y + positioning.height + 5 + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width * 0.80 + "px";
    }
    if (this.element_.classList.contains(this.cssClasses_.TOP)) {
        this.element_.style.top = positioning.y - (tooltipPosition.height + 15) + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width / 2 + "px";
    } else if (this.element_.classList.contains(this.cssClasses_.TOP_LEFT)) {
        this.element_.style.top = positioning.y - (tooltipPosition.height + 15) + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width * 0.08 + "px";
    } else if (this.element_.classList.contains(this.cssClasses_.TOP_RIGHT)) {
        this.element_.style.top = positioning.y - (tooltipPosition.height + 15) + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width * 0.80 + "px";
    }
    if (this.element_.classList.contains(this.cssClasses_.LEFT)) {
        this.element_.style.top = positioning.y + positioning.height / 2 - tooltipPosition.height / 2 + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width - 15 + "px";
    } else if (this.element_.classList.contains(this.cssClasses_.LEFT_TOP)) {
        this.element_.style.top = positioning.y + positioning.height / 2 - tooltipPosition.height * 0.80 + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width - 15 + "px";
    } else if (this.element_.classList.contains(this.cssClasses_.LEFT_BOTTOM)) {
        this.element_.style.top = positioning.y + positioning.height / 2 - tooltipPosition.height * 0.30 + "px";
        this.element_.style.left = positioning.x - tooltipPosition.width - 15 + "px";
    }
    if (this.element_.classList.contains(this.cssClasses_.RIGHT)) {
        this.element_.style.top = positioning.y + positioning.height / 2 - tooltipPosition.height / 2 + "px";
        this.element_.style.left = positioning.x + positioning.width + 15 + "px";
    } else if (this.element_.classList.contains(this.cssClasses_.RIGHT_TOP)) {
        this.element_.style.top = positioning.y + positioning.height / 2 - tooltipPosition.height * 0.80 + "px";
        this.element_.style.left = positioning.x + positioning.width + 15 + "px";
    } else if (this.element_.classList.contains(this.cssClasses_.RIGHT_BOTTOM)) {
        this.element_.style.top = positioning.y + positioning.height / 2 - tooltipPosition.height * 0.30 + "px";
        this.element_.style.left = positioning.x + positioning.width + 15 + "px";
    }
};
// The component registers itself. It can assume componentHandler is available
// in the global scape.
componentHandler.register({
    constructor: KomodoTooltip,
    classAsString: 'KomodoTooltip',
    cssClass: 'kmd-tooltip',
    widget: true
});
function initKomodo() {
    'use strict';
    if ('classList' in document.createElement('div') && 'querySelector' in document && 'addEventListener' in window && Array.prototype.forEach) {
        componentHandler.upgradeAllRegistered();
    } else {
        // 
        // Dummy function to avoid JS errors.
        // 
        componentHandler.upgradeElement = function() {};
        // 
        // Dummy function to avoid JS errors.
        componentHandler.register = function() {};
    }
    var observer = new MutationObserver(function(mutations) {
        window.componentHandler.upgradeDom();
    });
    observer.observe(document, {
        childList: true,
        subtree: true
    });
}
if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initKomodo();
} else {
    document.addEventListener('DOMContentLoaded', initKomodo);
}
// Limit the characters for elements with the class 'kmd-text-maxlength-50'
// function limitCharacters(limits) {
//   for (var className in limits) {
//       var maxCharacters = limits[className];
//       var elements = document.querySelectorAll('.' + className);
//       elements.forEach(function(element) {
//           var content = element.textContent || element.innerText;
//           if (content.length > maxCharacters) {
//               element.textContent = content.substring(0, maxCharacters) + '...';
//           }
//       });
//   }
// }
// Define the limits for each class
// var classCharacterLimits = {
//   'kmd-text-maxlength-50': 50,
//   'kmd-text-maxlength-90': 84
// };
// Call the function to limit the characters for elements with the specified classes
// limitCharacters(classCharacterLimits);
function addSoftHyphens(hypenText) {
    return hypenText.replace(/([a-zA-Z]{5})/g, '$1&shy;');
}
document.addEventListener("DOMContentLoaded", function() {
    var elements = document.querySelectorAll(".kmd-text-sml-maxlength");
    for(var i = 0; i < elements.length; i++){
        elements[i].innerHTML = addSoftHyphens(elements[i].innerHTML);
    }
});
